import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  createContext,
  useContext,
  Suspense,
  lazy,
  Component,
} from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { supabase } from "./supabaseClient";
import AuthPage from "./AuthPage";
import { formatDate, formatDateTime } from "./lib/utils";
import "./App.css";

// Import Shadcn UI components
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";
import { Toaster } from "./components/ui/toaster";
import { toast } from "./hooks/use-toast";

// Icons
import {
  Users,
  FileText,
  TrendingUp,
  LogOut,
  Plus,
  Eye,
  Trash2,
  AlertTriangle,
  Menu,
  X,
  MapPin,
  User,
  Pencil,
  Download,
  Bell,
  AlertCircle,
  Train,
  Moon,
  Sun,
  Settings,
  UserCheck,
  LayoutDashboard,
  MoreHorizontal,
  ChevronDown,
  ChevronRight,
  Target,
  ArrowLeftRight,
  Building2,
  PackageCheck,
} from "lucide-react";

// Lazy-loaded tab components (code splitting)
const ClientsTab = lazy(() =>
  import("./components/ClientsTab").then((m) => ({ default: m.ClientsTab }))
);
const TransactionsTab = lazy(() =>
  import("./components/TransactionsTab").then((m) => ({
    default: m.TransactionsTab,
  }))
);
const DashboardTab = lazy(() =>
  import("./components/DashboardTab").then((m) => ({ default: m.DashboardTab }))
);
const RailwayChallansTab = lazy(() =>
  import("./components/RailwayChallansTab").then((m) => ({
    default: m.RailwayChallansTab,
  }))
);
const RailwayCoachingTab = lazy(() =>
  import("./components/RailwayCoachingTab").then((m) => ({
    default: m.RailwayCoachingTab,
  }))
);
const AdminPanelTab = lazy(() =>
  import("./components/AdminPanelTab").then((m) => ({
    default: m.AdminPanelTab,
  }))
);
const AnalyticsTab = lazy(() =>
  import("./components/AnalyticsTab").then((m) => ({
    default: m.AnalyticsTab,
  }))
);
const SettingsTab = lazy(() =>
  import("./components/SettingsTab").then((m) => ({
    default: m.SettingsTab,
  }))
);
const CustodyPilotTab = lazy(() =>
  import("./components/CustodyPilotTab").then((m) => ({
    default: m.CustodyPilotTab,
  }))
);
import { NotificationBell } from "./components/NotificationBell";
import { KlaroWordmark } from "./components/KlaroWordmark";
import SignatureCanvas from "react-signature-canvas";

// --- ERROR BOUNDARY — catches any render crash and shows a recovery UI instead of blank screen ---
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'sans-serif', gap:'12px', padding:'24px', textAlign:'center' }}>
          <p style={{ color:'hsl(var(--destructive))', fontSize:'15px' }}>Something went wrong. Please refresh the page.</p>
          <button onClick={() => window.location.reload()} style={{ padding:'8px 20px', background:'hsl(var(--klaro-blue))', color:'hsl(var(--primary-foreground))', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'14px' }}>
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Fire-and-forget ping to warm up the Vercel Python function.
// Runs immediately on JS module load — warms the lambda while Supabase auth check is in flight.
try { axios.get('/api/ping').catch(() => {}); } catch (_) {}

const API = "/api";

// --- LOADING BAR CONTEXT ---
export const LoadingBarContext = createContext();
export const useLoadingBar = () => useContext(LoadingBarContext);

const LoadingBarProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);

  const startLoading = () => {
    setIsLoading(true);
    setProgress(10);
    timerRef.current = setInterval(() => {
      setProgress((p) => {
        if (p >= 85) {
          clearInterval(timerRef.current);
          return 85;
        }
        return p + Math.random() * 8;
      });
    }, 200);
  };

  const stopLoading = () => {
    clearInterval(timerRef.current);
    setProgress(100);
    setTimeout(() => {
      setIsLoading(false);
      setProgress(0);
    }, 300);
  };

  return (
    <LoadingBarContext.Provider value={{ startLoading, stopLoading }}>
      {isLoading && (
        <div className="fixed top-0 left-0 right-0 z-[9999] h-[3px]">
          <div
            className="h-full transition-all duration-200 ease-out"
            style={{ width: `${progress}%`, backgroundColor: "hsl(var(--klaro-clarity-blue))" }}
          />
        </div>
      )}
      {children}
    </LoadingBarContext.Provider>
  );
};

axios.interceptors.request.use(async (config) => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`;
  }
  return config;
});

// Retry failed requests up to 3 times with exponential back-off (skip 4xx errors)
axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const config = error.config;
    if (!config) return Promise.reject(error);
    const status = error.response?.status;
    // Only retry on network errors or 5xx; never retry 4xx (auth/validation failures)
    if (status && status < 500) return Promise.reject(error);
    config._retryCount = (config._retryCount || 0) + 1;
    if (config._retryCount > 3) return Promise.reject(error);
    await new Promise((r) => setTimeout(r, 400 * Math.pow(2, config._retryCount - 1)));
    return axios(config);
  }
);

// --- AUTH CONTEXT ---
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

const AuthProvider = ({ children, session }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingApproval, setPendingApproval] = useState(false);
  const _idleTimer = useRef(null);
  const _idleMinutes = useRef(60);

  // Load session timeout from server settings once on mount
  useEffect(() => {
    axios.get('/settings/session').then(r => {
      _idleMinutes.current = parseInt(r.data.auto_logout_minutes) || 60;
    }).catch(() => {});
  }, []);

  // Inactivity auto-logout: reset timer on any user activity
  useEffect(() => {
    if (!user) return;
    const _resetTimer = () => {
      clearTimeout(_idleTimer.current);
      _idleTimer.current = setTimeout(async () => {
        await supabase.auth.signOut();
        setUser(null);
        setPendingApproval(false);
      }, _idleMinutes.current * 60 * 1000);
    };
    const _events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    _events.forEach(e => window.addEventListener(e, _resetTimer, { passive: true }));
    _resetTimer(); // start timer immediately
    return () => {
      clearTimeout(_idleTimer.current);
      _events.forEach(e => window.removeEventListener(e, _resetTimer));
    };
  }, [user]);

  useEffect(() => {
    if (session) {
      fetchUser(session);
    } else {
      setUser(null);
      setLoading(false);
    }
  }, [session]);

  const fetchUser = async (currentSession) => {
    setLoading(true);
    setPendingApproval(false);
    let lastError = null;
    // Retry up to 4 times on 503 (cold-start / DB not ready) with backoff
    for (let attempt = 0; attempt < 4; attempt++) {
      if (attempt > 0) {
        await new Promise((r) => setTimeout(r, 600 * attempt));
      }
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${currentSession.access_token}` },
          timeout: 10000,
          _retryCount: 99, // skip global retry interceptor — we handle retries here
        });
        setUser(response.data);
        setLoading(false);
        return;
      } catch (error) {
        lastError = error;
        const status = error.response?.status;
        if (status === 503 || status === 502 || !status) {
          // Server unavailable / cold start — retry
          continue;
        }
        if (status === 403 && error.response?.data?.detail?.includes("pending")) {
          setPendingApproval(true);
          setUser(null);
          setLoading(false);
          return;
        }
        // Any other error (401, 400, etc.) — don't retry
        break;
      }
    }
    console.error("Auth error after retries:", lastError?.message);
    setUser(null);
    setLoading(false);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setPendingApproval(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, pendingApproval }}>
      {children}
    </AuthContext.Provider>
  );
};

// --- COMPANY PROFILE CONTEXT ---
const CompanyProfileContext = createContext({ company_name: 'Klaro by Kalsi', logo_base64: null, tagline: '', refreshProfile: () => {} });
export const useCompanyProfile = () => useContext(CompanyProfileContext);

const CompanyProfileProvider = ({ children }) => {
  const [profile, setProfile] = useState({ company_name: 'Klaro by Kalsi', logo_base64: null, tagline: '' });

  const fetchProfile = () => {
    axios.get(`${API}/company-profile/public`)
      .then(res => setProfile(res.data))
      .catch(() => {});
  };

  useEffect(() => { fetchProfile(); }, []);

  return (
    <CompanyProfileContext.Provider value={{ ...profile, refreshProfile: fetchProfile }}>
      {children}
    </CompanyProfileContext.Provider>
  );
};

// --- THEME CONTEXT ---
const ThemeContext = createContext();
export const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("darkMode") === "true",
  );

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

// --- SUB-COMPONENTS ---
const LiveClock = () => {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const istTime = new Date(
    time.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }),
  );
  return (
    <div className="mt-3 p-2 rounded-lg text-center" style={{ background: "hsl(var(--primary-foreground) / 0.08)" }}>
      <div className="text-xs font-medium" style={{ color: "hsl(var(--primary-foreground) / 0.55)" }}>
        New Delhi, India
      </div>
      <div className="text-base font-bold text-white">
        {istTime.toLocaleTimeString("en-IN", { hour12: true })}
      </div>
    </div>
  );
};

const DesktopSidebar = ({ currentView, setCurrentView }) => {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const companyProfile = useCompanyProfile();

  const railwayViews = ["railway", "railway-coaching"];
  // Railway group is collapsed by default; auto-open when a railway view is active
  const [railwayOpen, setRailwayOpen] = useState(railwayViews.includes(currentView));
  useEffect(() => {
    if (["railway", "railway-coaching"].includes(currentView)) setRailwayOpen(true);
  }, [currentView]);

  const groups = [
    {
      label: "Operations",
      items: [
        { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, adminOnly: true },
        { id: "transactions", label: "Transactions", icon: FileText, adminOnly: false },
        { id: "custody", label: "Custody Pilot", icon: PackageCheck, custodyOnly: true },
        { id: "analytics", label: "Analytics", icon: TrendingUp, adminOnly: true },
      ],
    },
    {
      label: "Clients & Contracts",
      items: [
        { id: "clients", label: "Clients & Contracts", icon: Users, adminOnly: true },
      ],
    },
    {
      label: "Railway",
      collapsible: true,
      items: [
        { id: "railway", label: "Railway Challans", icon: Train, adminOnly: false },
        { id: "railway-coaching", label: "Railway Coaching", icon: Train, adminOnly: false, clientHide: true },
      ],
    },
    {
      label: "Administration",
      items: [
        { id: "admin", label: "Admin Panel", icon: UserCheck, adminOnly: true },
        { id: "settings", label: "Settings", icon: Settings, adminOnly: true },
        { id: "company-profile", label: "Company Profile", icon: Building2, adminOnly: true },
      ],
    },
  ];

  const filterItems = (items) =>
    items.filter(
      (item) =>
        (!item.adminOnly || user?.role === "admin") &&
        (!item.custodyOnly || ["admin", "operator", "worker"].includes(user?.role)) &&
        (user?.role !== "worker" || item.id === "custody") &&
        !(item.clientHide && user?.role === "client"),
    );

  const itemButton = (item) => {
    const active = currentView === item.id;
    return (
      <button
        key={item.id}
        onClick={() => setCurrentView(item.id)}
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left"
        style={active
          ? { backgroundColor: "hsl(var(--klaro-blue))", color: "hsl(var(--primary-foreground))" }
          : { color: "hsl(var(--primary-foreground) / 0.65)" }
        }
        onMouseEnter={e => { if (!active) { e.currentTarget.style.backgroundColor = "hsl(var(--primary-foreground) / 0.08)"; e.currentTarget.style.color = "hsl(var(--primary-foreground))"; } }}
        onMouseLeave={e => { if (!active) { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "hsl(var(--primary-foreground) / 0.65)"; } }}
      >
        <item.icon className="h-4 w-4 flex-shrink-0" />
        {item.label}
      </button>
    );
  };

  return (
    <div className="hidden md:flex w-64 flex-col shadow-xl" style={{ backgroundColor: "hsl(var(--klaro-deep-blue))" }}>
      {/* Brand header */}
      <div className="px-4 pt-4 pb-3" style={{ borderBottom: "1px solid hsl(var(--primary-foreground) / 0.1)" }}>
        {/* Primary: Klaro wordmark */}
        <KlaroWordmark size="lg" theme="dark" />

        {/* Secondary: "by Kalsi" + logo — smaller, clearly subordinate */}
        <div className="flex items-center gap-1.5 mt-0.5 mb-3">
          <span style={{ fontSize: "0.7rem", color: "hsl(var(--primary-foreground) / 0.45)", fontFamily: "'Inter', Arial, sans-serif", fontWeight: 500, letterSpacing: "0.01em" }}>
            by Kalsi
          </span>
          {companyProfile.logo_base64 && (
            <img
              src={companyProfile.logo_base64}
              alt="Kalsi"
              style={{ height: "1rem", width: "auto", objectFit: "contain", opacity: 0.45, filter: "brightness(0) invert(1)" }}
            />
          )}
        </div>

        {/* User info row + notification bell — with clear spacing between them */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-white truncate">{user?.name || "User"}</p>
            <p className="text-[10px] capitalize" style={{ color: "hsl(var(--primary-foreground) / 0.5)" }}>{user?.role || "user"}</p>
          </div>
          <div className="flex-shrink-0">
            <NotificationBell />
          </div>
        </div>

        <LiveClock />
      </div>

      {/* Navigation — grouped */}
      <nav className="flex-1 p-3 space-y-3 overflow-y-auto">
        {groups.map((group) => {
          const items = filterItems(group.items);
          if (items.length === 0) return null;

          if (group.collapsible) {
            const groupActive = items.some((i) => i.id === currentView);
            return (
              <div key={group.label}>
                <button
                  onClick={() => setRailwayOpen((o) => !o)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left"
                  style={{ color: groupActive ? "hsl(var(--primary-foreground))" : "hsl(var(--primary-foreground) / 0.65)" }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = "hsl(var(--primary-foreground) / 0.08)"; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; }}
                >
                  <span className="flex items-center gap-2.5">
                    <Train className="h-4 w-4 flex-shrink-0" />
                    {group.label}
                  </span>
                  {railwayOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                {railwayOpen && (
                  <div className="mt-0.5 ml-3 pl-3 space-y-0.5" style={{ borderLeft: "1px solid hsl(var(--primary-foreground) / 0.15)" }}>
                    {items.map(itemButton)}
                  </div>
                )}
              </div>
            );
          }

          return (
            <div key={group.label}>
              <p
                className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider"
                style={{ color: "hsl(var(--primary-foreground) / 0.35)" }}
              >
                {group.label}
              </p>
              <div className="space-y-0.5">{items.map(itemButton)}</div>
            </div>
          );
        })}
      </nav>

      {/* Footer actions */}
      <div className="p-3 space-y-0.5" style={{ borderTop: "1px solid hsl(var(--primary-foreground) / 0.1)" }}>
        <button
          onClick={toggleDarkMode}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
          style={{ color: "hsl(var(--primary-foreground) / 0.65)" }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = "hsl(var(--primary-foreground) / 0.08)"; e.currentTarget.style.color = "hsl(var(--primary-foreground))"; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "hsl(var(--primary-foreground) / 0.65)"; }}
        >
          {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          {darkMode ? "Light mode" : "Dark mode"}
        </button>
        <button
          onClick={logout}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
          style={{ color: "hsl(var(--primary-foreground) / 0.65)" }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = "hsl(var(--primary-foreground) / 0.08)"; e.currentTarget.style.color = "hsl(var(--primary-foreground))"; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "hsl(var(--primary-foreground) / 0.65)"; }}
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </div>
  );
};

const MobileBottomNav = ({ currentView, setCurrentView }) => {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const [showMore, setShowMore] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const allItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      adminOnly: true,
    },
    {
      id: "transactions",
      label: "Transactions",
      icon: FileText,
      adminOnly: false,
    },
    { id: "custody", label: "Custody", icon: PackageCheck, custodyOnly: true },
    { id: "clients", label: "Clients", icon: Users, adminOnly: true },
    { id: "analytics", label: "Analytics", icon: TrendingUp, adminOnly: true },
    { id: "railway", label: "Challans", icon: Train, adminOnly: false },
    {
      id: "railway-coaching",
      label: "Coaching",
      icon: Train,
      adminOnly: false,
      clientHide: true,
    },
    { id: "admin", label: "Admin", icon: UserCheck, adminOnly: true },
    { id: "settings", label: "Settings", icon: Settings, adminOnly: true },
    { id: "company-profile", label: "Company", icon: Building2, adminOnly: true },
  ];

  const visibleItems = allItems.filter(
    (item) =>
      (!item.adminOnly || user?.role === "admin") &&
      (!item.custodyOnly || ["admin", "operator", "worker"].includes(user?.role)) &&
      (user?.role !== "worker" || item.id === "custody") &&
      !(item.clientHide && user?.role === "client"),
  );

  const primaryItems = visibleItems.slice(0, 3);
  const overflowItems = visibleItems.slice(3);

  return (
    <>
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 pb-safe">
        <nav className="flex items-center justify-around h-16">
          {primaryItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setCurrentView(item.id);
                setShowMore(false);
              }}
              className={`flex flex-col items-center justify-center flex-1 h-full min-w-[64px] touch-target transition-colors`}
              style={{ color: currentView === item.id ? "hsl(var(--klaro-clarity-blue))" : "hsl(var(--klaro-slate))" }}
            >
              <item.icon className="h-5 w-5" />
              <span className="text-[10px] mt-1 font-medium leading-none">
                {item.label}
              </span>
            </button>
          ))}
          {overflowItems.length > 0 && (
            <button
              onClick={() => setShowMore(!showMore)}
              className="flex flex-col items-center justify-center flex-1 h-full min-w-[64px] touch-target transition-colors"
              style={{ color: (showMore || overflowItems.some((i) => i.id === currentView)) ? "hsl(var(--klaro-clarity-blue))" : "hsl(var(--klaro-slate))" }}
            >
              <MoreHorizontal className="h-5 w-5" />
              <span className="text-[10px] mt-1 font-medium leading-none">
                More
              </span>
            </button>
          )}
        </nav>
      </div>

      {showMore && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowMore(false)}
          />
          <div className="md:hidden fixed bottom-16 left-0 right-0 bg-card border-t border-border z-50 rounded-t-2xl shadow-2xl pb-safe">
            <div className="p-4 space-y-1">
              {overflowItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setCurrentView(item.id);
                    setShowMore(false);
                  }}
                  className="flex items-center gap-3 w-full p-3 rounded-lg touch-target transition-colors text-sm font-medium"
                  style={currentView === item.id
                    ? { backgroundColor: "hsl(var(--klaro-mist))", color: "hsl(var(--klaro-deep-blue))" }
                    : { color: "hsl(var(--foreground))" }}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </button>
              ))}
              <div className="border-t border-border pt-2 mt-2 space-y-1">
                <button
                  onClick={() => { setShowProfile(true); setShowMore(false); }}
                  className="flex items-center gap-3 w-full p-3 rounded-lg touch-target text-foreground hover:bg-muted transition-colors text-sm font-medium"
                >
                  <User className="h-5 w-5" />
                  <span>Profile</span>
                </button>
                <button
                  onClick={toggleDarkMode}
                  className="flex items-center gap-3 w-full p-3 rounded-lg touch-target text-foreground hover:bg-muted transition-colors text-sm font-medium"
                >
                  {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                  <span>{darkMode ? "Light mode" : "Dark mode"}</span>
                </button>
                <button
                  onClick={logout}
                  className="flex items-center gap-3 w-full p-3 rounded-lg touch-target hover:bg-muted transition-colors text-sm font-medium"
                  style={{ color: "hsl(var(--klaro-slate))" }}
                >
                  <LogOut className="h-5 w-5" />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      <ProfileSettings
        open={showProfile}
        onClose={() => setShowProfile(false)}
      />
    </>
  );
};

const PendingApprovalScreen = () => {
  const { logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  return (
    <div className="h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Account pending approval</CardTitle>
          <CardDescription>
            Your account has been created. Please wait for an admin to approve
            your access and assign your role.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center gap-2">
          <Button variant="outline" onClick={toggleDarkMode}>
            {darkMode ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </Button>
          <Button variant="destructive" onClick={logout}>
            <LogOut className="mr-2 h-4 w-4" /> Sign Out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

const ProfileSettings = ({ open, onClose }) => {
  const { user } = useAuth();
  const [signature, setSignature] = useState(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [loading, setLoading] = useState(false);
  const signatureRef = useRef(null);

  useEffect(() => {
    if (open) {
      fetchSignature();
    }
  }, [open]);

  const fetchSignature = async () => {
    try {
      const res = await axios.get(`${API}/users/me`);
      if (res.data.signature) {
        setSignature(res.data.signature);
        setHasSignature(true);
      } else {
        setSignature(null);
        setHasSignature(false);
      }
    } catch (error) {
      console.error("Failed to fetch signature:", error?.message);
    }
  };

  const handleSaveSignature = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast({
        title: "Error",
        description: "Please draw your signature first",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      const signatureData = signatureRef.current.toDataURL("image/png");
      await axios.put(`${API}/users/signature`, { signature: signatureData });
      setSignature(signatureData);
      setHasSignature(true);
      toast({
        title: "Success",
        description: "Signature saved to your profile",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save signature",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSignature = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/users/signature`);
      setSignature(null);
      setHasSignature(false);
      if (signatureRef.current) signatureRef.current.clear();
      toast({
        title: "Success",
        description: "Signature removed from your profile",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete signature",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    if (signatureRef.current) signatureRef.current.clear();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profile Settings
          </DialogTitle>
          <DialogDescription>
            Manage your profile and signature
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 bg-muted rounded-lg">
            <p className="font-medium">{user?.name}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {user?.email || user?.phone}
            </p>
            <Badge className="mt-1">{user?.role}</Badge>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block dark:text-slate-200">
              Your Signature
            </label>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
              This signature will be auto-filled when creating transactions
            </p>
            {hasSignature && signature ? (
              <div className="space-y-3">
                <div className="border border-border rounded-lg p-2 bg-card">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                    Current saved signature:
                  </p>
                  <img
                    src={signature}
                    alt="Saved signature"
                    className="max-h-24 mx-auto"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      setHasSignature(false);
                      setSignature(null);
                    }}
                  >
                    <Pencil className="h-4 w-4 mr-1" /> Update
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="flex-1"
                    onClick={handleDeleteSignature}
                    disabled={loading}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="border-2 border-dashed rounded-lg p-1">
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      className:
                        "w-full h-32 bg-white dark:bg-slate-100 rounded",
                    }}
                    penColor="black"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleClear}>
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveSignature}
                    disabled={loading}
                    className="flex-1"
                  >
                    Save Signature
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PortalChallanView = ({ transaction, company_name, logo_base64, tagline }) => {
  if (!transaction) return null;
  return (
    <div className="bg-white p-6 text-sm">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-shrink-0">
          {logo_base64 ? (
            <img src={logo_base64} alt="Company Logo" className="h-14 w-auto" />
          ) : (
            <div className="h-14 w-14 bg-slate-100 rounded flex items-center justify-center text-xs text-slate-400">Logo</div>
          )}
        </div>
        <div className="text-center flex-1 mx-6">
          <h1 className="text-xl font-bold" style={{ color: "hsl(var(--klaro-deep-blue))" }}>{company_name}</h1>
          {tagline && <p className="text-xs text-slate-500 mt-0.5">{tagline}</p>}
          <h2 className="text-base font-bold mt-2">
            {transaction.transaction_type === 'pickup' ? 'SOILED LINEN PICKUP CHALLAN' : 'CLEAN LINEN DELIVERY CHALLAN'}
          </h2>
        </div>
        <div className="w-14 flex-shrink-0" />
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 mb-4 border rounded p-3 bg-slate-50 text-xs">
        <div><span className="font-medium">Challan No.:</span> {transaction.challan_no}</div>
        <div><span className="font-medium">Date & Time:</span> {formatDateTime(transaction.timestamp)}</div>
        <div><span className="font-medium">Location:</span> {transaction.location_name}</div>
        {transaction.airline_name && <div><span className="font-medium">Airline:</span> {transaction.airline_name}</div>}
        {transaction.operator_name && <div><span className="font-medium">Operator:</span> {transaction.operator_name}</div>}
        {transaction.client_representative_name && <div><span className="font-medium">Client Rep.:</span> {transaction.client_representative_name}</div>}
      </div>

      {/* Items Table */}
      {transaction.items && transaction.items.length > 0 && (
        <div className="border rounded overflow-hidden mb-4">
          <table className="w-full text-xs">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">S.No</th>
                <th className="text-left px-3 py-2 font-semibold">Item Name</th>
                <th className="text-right px-3 py-2 font-semibold">Prev. Balance</th>
                {transaction.transaction_type === 'pickup' && <th className="text-right px-3 py-2 font-semibold">New Issued</th>}
                <th className="text-right px-3 py-2 font-semibold">
                  {transaction.transaction_type === 'pickup' ? 'Soiled Received' : 'Clean Delivered'}
                </th>
                <th className="text-right px-3 py-2 font-semibold">Closing Balance</th>
              </tr>
            </thead>
            <tbody>
              {transaction.items.map((item, idx) => (
                <tr key={idx} className={`border-t ${item.carry_forward ? 'text-slate-400 italic bg-slate-50' : ''}`}>
                  <td className="px-3 py-1.5">{idx + 1}</td>
                  <td className="px-3 py-1.5">{item.item_name}{item.carry_forward ? ' (c/f)' : ''}</td>
                  <td className="px-3 py-1.5 text-right">{item.previous_balance}</td>
                  {transaction.transaction_type === 'pickup' && <td className="px-3 py-1.5 text-right">{item.new_issued_qty || 0}</td>}
                  <td className="px-3 py-1.5 text-right">
                    {transaction.transaction_type === 'pickup' ? item.received_qty : item.delivered_qty}
                  </td>
                  <td className="px-3 py-1.5 text-right">{item.closing_balance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {transaction.notes && (
        <div className="text-xs text-slate-600 bg-slate-50 border rounded p-2 mb-4">
          <span className="font-medium">Notes: </span>{transaction.notes}
        </div>
      )}

      {/* Approval Status */}
      {transaction.approval_status && (
        <div className={`text-xs rounded p-2 mb-4 border ${
          transaction.approval_status === 'approved' ? 'bg-green-50 border-green-200 text-green-800' :
          transaction.approval_status === 'discrepancy' ? 'bg-amber-50 border-amber-200 text-amber-800' :
          'bg-slate-50 border-slate-200 text-slate-700'
        }`}>
          <span className="font-semibold">Status: </span>
          {transaction.approval_status === 'approved' ? 'Approved' :
           transaction.approval_status === 'discrepancy' ? 'Discrepancy Reported' : 'Pending Approval'}
          {transaction.approved_by && (
            <span className="ml-2">— Approved by {transaction.approved_by} on {formatDateTime(transaction.approved_at)}</span>
          )}
          {transaction.discrepancy_note && (
            <div className="mt-1"><span className="font-medium">Note: </span>{transaction.discrepancy_note}</div>
          )}
        </div>
      )}

      {/* Signatures */}
      <div className="grid grid-cols-2 gap-6 mt-4 border-t pt-4">
        <div className="text-center">
          <p className="text-xs font-medium mb-1">{transaction.operator_name || 'Kalsi Representative'}</p>
          {transaction.kalsi_signature && (
            <img src={transaction.kalsi_signature} alt="Kalsi Signature" className="h-16 w-full object-contain border rounded mb-1" />
          )}
          <div className="border-b border-slate-400 my-2" />
          <p className="text-xs font-semibold">Kalsi Representative</p>
          <p className="text-xs text-slate-500">Signature</p>
        </div>
        <div className="text-center">
          <p className="text-xs font-medium mb-1">{transaction.client_representative_name || 'Client Representative'}</p>
          {transaction.client_signature ? (
            <img src={transaction.client_signature} alt="Client Signature" className="h-16 w-full object-contain border rounded mb-1" />
          ) : transaction.approved_by ? (
            <div className="text-green-600 font-medium text-xs py-4">Approved by {transaction.approved_by}</div>
          ) : null}
          <div className="border-b border-slate-400 my-2" />
          <p className="text-xs font-semibold">Client Representative</p>
          <p className="text-xs text-slate-500">Signature</p>
          {transaction.portal_approver_name && (
            <p className="text-xs mt-1" style={{ color: 'hsl(var(--klaro-clarity-blue))' }}>Portal Approver: {transaction.portal_approver_name}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const CollapsibleTransactionCard = ({ tx, defaultOpen, getStatusBadge, onView, onDownloadPDF, onApprove, onReportIssue, onReportVariance }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const dateStr = formatDate(tx.timestamp);
  const timeStr = new Date(tx.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  const isPickup = tx.transaction_type === "pickup";

  const itemRows = (tx.items || []).filter(it => !it.carry_forward);

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Collapsible Header */}
      <button
        className="w-full text-left px-3 py-2.5 flex items-center gap-2 hover:bg-muted transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs font-semibold" style={{ color: 'hsl(var(--klaro-clarity-blue))' }}>{tx.challan_no}</span>
            <Badge variant="outline" className={`text-xs capitalize ${isPickup ? "border-amber-400 text-amber-600" : "border-emerald-400 text-emerald-600"}`}>
              {tx.transaction_type}
            </Badge>
            {getStatusBadge(tx.approval_status)}
            <span className="text-xs text-slate-500 dark:text-slate-400 hidden sm:inline">
              {tx.location_name}
            </span>
          </div>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 flex items-center gap-1 flex-wrap">
            <span className="sm:hidden truncate max-w-[120px]">{tx.location_name}</span>
            <span className="sm:hidden">·</span>
            <span>{dateStr}, {timeStr}</span>
            {tx.client_representative_name && (
              <><span>·</span><span className="truncate max-w-[100px]">{tx.client_representative_name}</span></>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            onClick={e => { e.stopPropagation(); onView(); }}
            title="View full challan"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
          <button
            className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            onClick={e => { e.stopPropagation(); onDownloadPDF(); }}
            title="Download PDF"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
          <span className={`text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}>
            <ChevronDown className="h-4 w-4" />
          </span>
        </div>
      </button>

      {/* Expanded Content */}
      {open && (
        <div className="border-t border-border px-3 pb-3 pt-2">
          {itemRows.length > 0 && (
            <div className="overflow-x-auto mb-2">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-muted-foreground border-b border-border">
                    <th className="text-left py-1 pr-2 font-medium w-6">#</th>
                    <th className="text-left py-1 pr-2 font-medium">Item</th>
                    {isPickup ? (
                      <>
                        <th className="text-right py-1 pr-2 font-medium">Prev Bal</th>
                        <th className="text-right py-1 pr-2 font-medium">Received</th>
                        <th className="text-right py-1 font-medium">Closing</th>
                      </>
                    ) : (
                      <>
                        <th className="text-right py-1 pr-2 font-medium">Prev Bal</th>
                        <th className="text-right py-1 pr-2 font-medium">Delivered</th>
                        <th className="text-right py-1 font-medium">Closing</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {itemRows.map((it, i) => (
                    <tr key={i} className="border-b dark:border-slate-800 last:border-0">
                      <td className="py-1 pr-2 text-slate-400">{i + 1}</td>
                      <td className="py-1 pr-2 text-slate-700 dark:text-slate-300">{it.item_name}</td>
                      {isPickup ? (
                        <>
                          <td className="py-1 pr-2 text-right text-slate-600 dark:text-slate-400">{it.previous_balance ?? 0}</td>
                          <td className="py-1 pr-2 text-right text-slate-600 dark:text-slate-400">{it.received_qty ?? 0}</td>
                          <td className="py-1 text-right font-semibold text-slate-800 dark:text-slate-200">{it.closing_balance ?? 0}</td>
                        </>
                      ) : (
                        <>
                          <td className="py-1 pr-2 text-right text-slate-600 dark:text-slate-400">{it.previous_balance ?? 0}</td>
                          <td className="py-1 pr-2 text-right text-slate-600 dark:text-slate-400">{it.delivered_qty ?? 0}</td>
                          <td className="py-1 text-right font-semibold text-slate-800 dark:text-slate-200">{it.closing_balance ?? 0}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {tx.approval_status === "pending_approval" && (
            <div className="flex gap-2 mt-1">
              <Button
                size="sm"
                className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-700 text-white"
                onClick={onApprove}
              >
                Approve
              </Button>
              {!tx.discrepancy_note && (
                <Button
                  size="sm"
                  variant="destructive"
                  className="flex-1 h-7 text-xs"
                  onClick={onReportIssue}
                >
                  Report Issue
                </Button>
              )}
            </div>
          )}
          {tx.approval_state === "CONFIRMED" && (
            <div className="mt-1">
              <Button
                size="sm"
                variant="outline"
                className="w-full h-7 text-xs border-blue-400 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                onClick={onReportVariance}
              >
                Report Variance
              </Button>
            </div>
          )}
          {tx.approval_status === "approved" && tx.approval_state !== "CONFIRMED" && !tx.discrepancy_note && (
            <div className="mt-1">
              <Button
                size="sm"
                variant="outline"
                className="w-full h-7 text-xs border-amber-400 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                onClick={onReportIssue}
              >
                Raise Discrepancy
              </Button>
            </div>
          )}
          {tx.notes && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 italic">{tx.notes}</p>
          )}
        </div>
      )}
    </div>
  );
};

const KlaroDateInput = ({ value, onChange }) => (
  <input
    type="date"
    value={value}
    onChange={e => onChange(e.target.value)}
    className="w-full text-sm rounded-lg outline-none transition-all"
    style={{
      padding: "0.6rem 0.75rem",
      border: "1px solid hsl(var(--klaro-mist))",
      color: value ? "hsl(var(--klaro-deep-blue))" : "hsl(var(--klaro-slate))",
      backgroundColor: "hsl(var(--card))",
      fontFamily: "'Inter', Arial, sans-serif",
    }}
    onFocus={e => {
      e.target.style.borderColor = "hsl(var(--klaro-clarity-blue))";
      e.target.style.boxShadow = "0 0 0 3px hsl(var(--klaro-clarity-blue) / 0.15)";
    }}
    onBlur={e => {
      e.target.style.borderColor = "hsl(var(--klaro-mist))";
      e.target.style.boxShadow = "none";
    }}
  />
);

const ClientPortal = () => {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const { company_name, logo_base64, tagline } = useCompanyProfile();
  const [transactions, setTransactions] = useState([]);
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("transactions");
  const [approvalDialog, setApprovalDialog] = useState({
    open: false, transaction: null, approved: true, note: "", withSignature: false,
  });
  const [variancePreview, setVariancePreview] = useState(null);
  const [variancePreviewLoading, setVariancePreviewLoading] = useState(false);
  const [reportVarianceDialog, setReportVarianceDialog] = useState({
    open: false, transaction: null, note: "", items: [],
  });
  const [viewTransaction, setViewTransaction] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [reportDateRange, setReportDateRange] = useState({ start_date: "", end_date: "" });
  const [clientContracts, setClientContracts] = useState([]);
  const [selectedContractId, setSelectedContractId] = useState("all");
  const [summary, setSummary] = useState(null);
  // lossVariance removed in Phase 4 — variance diagnostics are admin-only
  const [timeframe, setTimeframe] = useState("this_month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [ledgerData, setLedgerData] = useState({});
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [allLocations, setAllLocations] = useState([]);
  const [parLevels, setParLevels] = useState([]);
  const [parInfo, setParInfo] = useState({});
  // Phase A — baseline-aware clean shelf stock (per-location, per-SKU)
  const [shelfStock, setShelfStock] = useState(null);
  const [parDialog, setParDialog] = useState({ open: false, locationId: "", skuId: "", contractId: "", proposed: 0, authoritative: 0, mode: "engine", overrideVal: "", history: [], loadingHistory: false, saving: false });

  // ── Flight loading (rep records what was loaded onto a flight) ──
  const todayStr = (() => {
    const d = new Date();
    const p = n => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  })();
  const [flightFormData, setFlightFormData] = useState({ locations: [], flights: [], aircraft_types: [] });
  const [flLocation, setFlLocation] = useState("");
  const [flFlightId, setFlFlightId] = useState("");
  const [flAircraftTypeId, setFlAircraftTypeId] = useState("");
  const [flDate, setFlDate] = useState(todayStr);
  const [flQtys, setFlQtys] = useState({});
  const [flSubmitting, setFlSubmitting] = useState(false);
  const [flLoadings, setFlLoadings] = useState([]);
  const [flHistoryLoading, setFlHistoryLoading] = useState(false);
  const [correctionDialog, setCorrectionDialog] = useState({ open: false, line: null, loading: null, value: "", saving: false });
  // ── Transfers (Phase 5) ──
  const [transferFormData, setTransferFormData] = useState({ locations: [], stock_by_location: {} });
  const [trFrom, setTrFrom] = useState("");
  const [trTo, setTrTo] = useState("");
  const [trQtys, setTrQtys] = useState({});
  const [trSubmitting, setTrSubmitting] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [trHistoryLoading, setTrHistoryLoading] = useState(false);
  const [receiveDialog, setReceiveDialog] = useState({ open: false, transfer: null, qtys: {}, saving: false });
  const [capabilities, setCapabilities] = useState(null);

  const pendingCount = transactions.filter(t => t.approval_status === "pending_approval").length;

  const getWindowDates = useCallback(() => {
    const today = new Date();
    const pad = n => String(n).padStart(2, "0");
    const fmt = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    if (timeframe === "all_time") return { from_date: "", to_date: "" };
    if (timeframe === "custom") return { from_date: customFrom, to_date: customTo };
    if (timeframe === "this_month") {
      return { from_date: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), to_date: fmt(today) };
    }
    if (timeframe === "last_month") {
      return {
        from_date: fmt(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        to_date: fmt(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
    }
    if (timeframe === "last_90_days") {
      const from = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
      return { from_date: fmt(from), to_date: fmt(today) };
    }
    return { from_date: "", to_date: "" };
  }, [timeframe, customFrom, customTo]);

  const getTimeframeLabel = useCallback(() => {
    const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    if (timeframe === "all_time") return "all time";
    if (timeframe === "this_month") { const d = new Date(); return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`; }
    if (timeframe === "last_month") { const d = new Date(); d.setMonth(d.getMonth() - 1); return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`; }
    if (timeframe === "last_90_days") return "last 90 days";
    const { from_date, to_date } = getWindowDates();
    if (from_date && to_date) return `${from_date} to ${to_date}`;
    return "selected period";
  }, [timeframe, getWindowDates]);

  const fetchData = useCallback(async () => {
    try {
      const qs = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const { from_date, to_date } = getWindowDates();
      const wqs = new URLSearchParams();
      if (from_date) wqs.set("from_date", from_date);
      if (to_date) wqs.set("to_date", to_date);
      if (selectedLocation !== "all") wqs.set("location", selectedLocation);
      const wStr = wqs.toString() ? `?${wqs.toString()}` : "";

      const txParams = new URLSearchParams();
      if (statusFilter !== "all") txParams.set("status", statusFilter);
      txParams.set("from_date", from_date);
      txParams.set("to_date", to_date);
      if (selectedLocation !== "all") txParams.set("location", selectedLocation);
      const txQs = `?${txParams.toString()}`;

      const [txRes, balRes, contractsRes, summaryRes, parRes, shelfStockRes] = await Promise.allSettled([
        axios.get(`${API}/client-portal/transactions${txQs}`),
        axios.get(`${API}/client-portal/balances${wStr}`),
        axios.get(`${API}/client-portal/contracts`),
        axios.get(`${API}/client-portal/summary${wStr}`),
        axios.get(`${API}/client-portal/par-levels`),
        axios.get(`${API}/client-portal/shelf-stock`),
      ]);
      if (txRes.status === "fulfilled") setTransactions(txRes.value.data);
      if (balRes.status === "fulfilled") {
        const balData = balRes.value.data;
        setBalances(balData.balances || {});
        setLedgerData(balData.ledger || {});
        if (selectedLocation === "all") {
          setAllLocations(balData.locations || Object.keys(balData.ledger || {}));
        }
      }
      const freshContracts = contractsRes.status === "fulfilled" ? (contractsRes.value.data || []) : [];
      if (contractsRes.status === "fulfilled") setClientContracts(freshContracts);
      if (summaryRes.status === "fulfilled") setSummary(summaryRes.value.data);
      if (parRes.status === "fulfilled") setParLevels(parRes.value.data || []);
      if (shelfStockRes.status === "fulfilled") setShelfStock(shelfStockRes.value.data);

      // ── Phase 6: authoritative par per location (proposed/authoritative/below-par) ──
      const ledgerLocations = balRes.status === "fulfilled"
        ? Object.keys(balRes.value.data.ledger || {})
        : [];
      const contractForLoc = (locId) => freshContracts.find(c =>
        (c.locations || []).some(l => (typeof l === "string" ? l : (l.id || l.name)) === locId)
      ) || freshContracts[0] || null;
      const pairs = [];
      const seen = new Set();
      ledgerLocations.forEach(loc => {
        const c = contractForLoc(loc);
        if (!c) return;
        const key = `${c.id}::${loc}`;
        if (seen.has(key)) return;
        seen.add(key);
        pairs.push({ contractId: c.id, location: loc });
      });
      const parResults = await Promise.allSettled(
        pairs.map(p => axios.get(`${API}/client-portal/par`, {
          params: { contract_id: p.contractId, location_id: p.location },
        }))
      );
      const nextParInfo = {};
      parResults.forEach((res, i) => {
        if (res.status !== "fulfilled") return;
        const { location, contractId } = pairs[i];
        (res.value.data.items || []).forEach(row => {
          nextParInfo[`${contractId}::${location}::${row.sku_id}`] = { ...row, contract_id: contractId };
        });
      });
      setParInfo(nextParInfo);
    } catch (err) {
      toast({ title: "Error loading portal", description: "Please refresh the page.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, getWindowDates, selectedLocation]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Phase 1: fetch portal capabilities once after client login ──
  useEffect(() => {
    axios.get(`${API}/client-portal/capabilities`)
      .then(res => setCapabilities(res.data))
      .catch(() => setCapabilities({ flight_operations: false, par_levels: false, transfers: false }));
  }, []);

  useEffect(() => {
    if (activeTab === "reports") {
      const { from_date, to_date } = getWindowDates();
      setReportDateRange({
        start_date: from_date || "",
        end_date: to_date || "",
      });
    }
  }, [activeTab, getWindowDates]);

  // ── Flight loading: load known flights, aircraft templates, and recent history ──
  const fetchFlightLoadingData = useCallback(async () => {
    setFlHistoryLoading(true);
    try {
      const [fdRes, histRes] = await Promise.allSettled([
        axios.get(`${API}/client-portal/flight-loadings/form-data`),
        axios.get(`${API}/client-portal/flight-loadings`),
      ]);
      if (fdRes.status === "fulfilled") {
        const d = fdRes.value.data || {};
        setFlightFormData({
          locations: d.locations || [],
          flights: d.flights || [],
          aircraft_types: d.aircraft_types || [],
        });
        setFlLocation(prev => prev || (d.locations?.length === 1 ? d.locations[0] : ""));
      }
      if (histRes.status === "fulfilled") setFlLoadings(histRes.value.data?.loadings || []);
    } catch (err) {
      toast({ title: "Couldn't load flight data", description: "Please refresh the page.", variant: "destructive" });
    } finally {
      setFlHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "loadflight") fetchFlightLoadingData();
  }, [activeTab, fetchFlightLoadingData]);

  const selectedAircraftType = flightFormData.aircraft_types.find(a => a.id === flAircraftTypeId) || null;

  const applyAircraftTemplate = (typeId) => {
    setFlAircraftTypeId(typeId);
    const at = flightFormData.aircraft_types.find(a => a.id === typeId);
    const next = {};
    (at?.items || []).forEach(it => { next[it.sku_id] = String(it.qty_per_flight ?? 0); });
    setFlQtys(next);
  };

  const handleSelectFlight = (id) => {
    setFlFlightId(id);
    const flight = flightFormData.flights.find(f => f.id === id);
    if (flight?.default_aircraft_type_id) applyAircraftTemplate(flight.default_aircraft_type_id);
  };

  const handleSubmitFlightLoading = async () => {
    if (!flLocation || !flFlightId || !flAircraftTypeId || !flDate) {
      toast({ title: "Missing details", description: "Pick a location, flight, date and aircraft type.", variant: "destructive" });
      return;
    }
    const lines = Object.entries(flQtys)
      .map(([sku_id, q]) => ({ sku_id, qty: parseInt(q, 10) || 0 }))
      .filter(l => l.qty > 0);
    if (lines.length === 0) {
      toast({ title: "Nothing to load", description: "Enter at least one quantity.", variant: "destructive" });
      return;
    }
    setFlSubmitting(true);
    try {
      await axios.post(`${API}/client-portal/flight-loadings`, {
        flight_id: flFlightId,
        aircraft_type_id: flAircraftTypeId,
        location_id: flLocation,
        flight_date: flDate,
        lines,
      });
      toast({ title: "Flight loaded", description: "Shelf stock has been updated." });
      setFlQtys({});
      setFlFlightId("");
      setFlAircraftTypeId("");
      fetchFlightLoadingData();
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "insufficient_store") {
        const list = (detail.shortfalls || [])
          .map(s => `${s.sku_id}: need ${s.requested_qty}, only ${s.available_qty} on shelf`)
          .join("; ");
        toast({ title: "Not enough stock on the shelf", description: list || detail.message, variant: "destructive" });
      } else {
        const msg = typeof detail === "object" ? detail?.message : detail;
        toast({ title: "Couldn't record loading", description: msg || "Please try again.", variant: "destructive" });
      }
    } finally {
      setFlSubmitting(false);
    }
  };

  const handleSubmitCorrection = async () => {
    const { line } = correctionDialog;
    if (!line) return;
    const qty = parseInt(correctionDialog.value, 10);
    if (isNaN(qty) || qty < 0) {
      toast({ title: "Enter a valid quantity", variant: "destructive" });
      return;
    }
    setCorrectionDialog(d => ({ ...d, saving: true }));
    try {
      await axios.post(`${API}/client-portal/flight-loadings/${line.movement_id}/correct`, { corrected_qty: qty });
      toast({ title: "Correction saved", description: "The record and shelf stock are updated." });
      setCorrectionDialog({ open: false, line: null, loading: null, value: "", saving: false });
      fetchFlightLoadingData();
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      toast({ title: "Couldn't save correction", description: msg || "Please try again.", variant: "destructive" });
      setCorrectionDialog(d => ({ ...d, saving: false }));
    }
  };

  // ── Transfers: load locations + per-location stock, and transfer history ──
  const fetchTransferData = useCallback(async () => {
    setTrHistoryLoading(true);
    try {
      const [fdRes, listRes] = await Promise.allSettled([
        axios.get(`${API}/client-portal/transfers/form-data`),
        axios.get(`${API}/client-portal/transfers`),
      ]);
      if (fdRes.status === "fulfilled") {
        const d = fdRes.value.data || {};
        setTransferFormData({
          locations: d.locations || [],
          stock_by_location: d.stock_by_location || {},
        });
        setTrFrom(prev => prev || (d.locations?.length === 1 ? d.locations[0] : ""));
      }
      if (listRes.status === "fulfilled") setTransfers(listRes.value.data?.transfers || []);
    } catch (err) {
      toast({ title: "Couldn't load transfer data", description: "Please refresh the page.", variant: "destructive" });
    } finally {
      setTrHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "transfers") fetchTransferData();
  }, [activeTab, fetchTransferData]);

  const trSourceStock = transferFormData.stock_by_location[trFrom] || [];

  const handleSubmitTransfer = async () => {
    if (!trFrom || !trTo) {
      toast({ title: "Missing details", description: "Pick where the stock is going from and to.", variant: "destructive" });
      return;
    }
    if (trFrom === trTo) {
      toast({ title: "Pick two different locations", description: "Source and destination must differ.", variant: "destructive" });
      return;
    }
    const lines = Object.entries(trQtys)
      .map(([sku_id, q]) => ({ sku_id, qty: parseInt(q, 10) || 0 }))
      .filter(l => l.qty > 0);
    if (lines.length === 0) {
      toast({ title: "Nothing to transfer", description: "Enter at least one quantity.", variant: "destructive" });
      return;
    }
    setTrSubmitting(true);
    try {
      await axios.post(`${API}/client-portal/transfers`, {
        from_location_id: trFrom,
        to_location_id: trTo,
        lines,
      });
      toast({ title: "Transfer sent", description: "Stock left the shelf and is now in transit." });
      setTrQtys({});
      setTrTo("");
      fetchTransferData();
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "insufficient_store") {
        const list = (detail.shortfalls || [])
          .map(s => `${s.sku_id}: need ${s.requested_qty}, only ${s.available_qty} on shelf`)
          .join("; ");
        toast({ title: "Not enough stock on the shelf", description: list || detail.message, variant: "destructive" });
      } else {
        const msg = typeof detail === "object" ? detail?.message : detail;
        toast({ title: "Couldn't send transfer", description: msg || "Please try again.", variant: "destructive" });
      }
    } finally {
      setTrSubmitting(false);
    }
  };

  const openReceiveDialog = (transfer) => {
    const qtys = {};
    (transfer.lines || []).forEach(l => { qtys[l.sku_id] = String(l.outstanding_qty); });
    setReceiveDialog({ open: true, transfer, qtys, saving: false });
  };

  const handleSubmitReceive = async () => {
    const { transfer, qtys } = receiveDialog;
    if (!transfer) return;
    const lines = Object.entries(qtys)
      .map(([sku_id, q]) => ({ sku_id, received_qty: parseInt(q, 10) || 0 }))
      .filter(l => l.received_qty > 0);
    if (lines.length === 0) {
      toast({ title: "Nothing to confirm", description: "Enter at least one received quantity.", variant: "destructive" });
      return;
    }
    setReceiveDialog(d => ({ ...d, saving: true }));
    try {
      await axios.post(`${API}/client-portal/transfers/${transfer.transfer_ref}/receive`, { lines });
      toast({ title: "Receipt confirmed", description: "Stock has been added to the shelf." });
      setReceiveDialog({ open: false, transfer: null, qtys: {}, saving: false });
      fetchTransferData();
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "over_receipt") {
        const list = (detail.over || [])
          .map(s => `${s.sku_id}: only ${s.outstanding_qty} in transit`)
          .join("; ");
        toast({ title: "More than is in transit", description: list || detail.message, variant: "destructive" });
      } else {
        const msg = typeof detail === "object" ? detail?.message : detail;
        toast({ title: "Couldn't confirm receipt", description: msg || "Please try again.", variant: "destructive" });
      }
      setReceiveDialog(d => ({ ...d, saving: false }));
    }
  };

  const handleApproval = async () => {
    const { transaction, approved, note, withSignature } = approvalDialog;
    if (!transaction) return;
    try {
      if (approved && !variancePreview) {
        setVariancePreviewLoading(true);
        try {
          const pvRes = await axios.get(`${API}/client-portal/transactions/${transaction.id}/approve-preview`);
          setVariancePreview(pvRes.data);
        } finally {
          setVariancePreviewLoading(false);
        }
        return;
      }
      await axios.put(`${API}/client-portal/transactions/${transaction.id}/approve`, {
        approved,
        discrepancy_note: note || null,
        client_signature: withSignature && user?.signature ? user.signature : null,
      });
      toast({ title: approved ? "Delivery confirmed" : "Flagged for review", description: approved ? "Thank you for confirming." : "We'll review it together and follow up." });
      setApprovalDialog(d => ({ ...d, open: false }));
      setVariancePreview(null);
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      toast({ title: "Could not submit", description: msg || "Please try again.", variant: "destructive" });
    }
  };

  const getContractForLocation = (locationId) => {
    return clientContracts.find(c =>
      (c.locations || []).some(l => (typeof l === 'string' ? l : l.name) === locationId)
    ) || null;
  };

  const getParInfo = (locationId, skuId) => {
    const c = getContractForLocation(locationId);
    if (c && parInfo[`${c.id}::${locationId}::${skuId}`]) return parInfo[`${c.id}::${locationId}::${skuId}`];
    const suffix = `::${locationId}::${skuId}`;
    const k = Object.keys(parInfo).find(key => key.endsWith(suffix));
    return k ? parInfo[k] : null;
  };

  const handleOpenParDialog = async (locationId, skuId) => {
    const info = getParInfo(locationId, skuId);
    const contract = getContractForLocation(locationId);
    const contractId = info?.contract_id || contract?.id || clientContracts[0]?.id || "";
    setParDialog({
      open: true, locationId, skuId, contractId,
      proposed: info?.proposed_qty ?? 0,
      authoritative: info?.authoritative_qty ?? 0,
      mode: info?.mode || "engine",
      overrideVal: info?.mode === "override" ? String(info?.authoritative_qty ?? "") : "",
      history: [], loadingHistory: true, saving: false,
    });
    if (!contractId) { setParDialog(d => ({ ...d, loadingHistory: false })); return; }
    try {
      const res = await axios.get(`${API}/client-portal/par/history`, {
        params: { contract_id: contractId, location_id: locationId, sku_id: skuId },
      });
      setParDialog(d => ({ ...d, history: res.data || [], loadingHistory: false }));
    } catch {
      setParDialog(d => ({ ...d, loadingHistory: false }));
    }
  };

  const submitParDecision = async (path, payload, successMsg) => {
    const { contractId, locationId, skuId } = parDialog;
    if (!contractId) return;
    setParDialog(d => ({ ...d, saving: true }));
    try {
      await axios.post(`${API}/client-portal/par/${path}`, {
        contract_id: contractId, location_id: locationId, sku_id: skuId, ...payload,
      });
      toast({ title: successMsg });
      setParDialog(d => ({ ...d, open: false, saving: false }));
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      toast({ title: "Could not update par", description: msg || "Please try again.", variant: "destructive" });
      setParDialog(d => ({ ...d, saving: false }));
    }
  };

  const handleParAccept = () =>
    submitParDecision("accept", {}, "Accepted the suggested par — it will keep adjusting with your schedule.");

  const handleParOverride = () => {
    const qty = parseInt(parDialog.overrideVal, 10);
    if (isNaN(qty) || qty < 0) { toast({ title: "Enter a valid quantity", variant: "destructive" }); return; }
    submitParDecision("override", { par_qty: qty }, `Par set to ${qty} (your fixed value).`);
  };

  const handleParRelease = () =>
    submitParDecision("release", {}, "Released back to the suggested par — it will adjust with your schedule again.");

  const handleReportVariance = async () => {
    const { transaction, note, items } = reportVarianceDialog;
    if (!transaction || !note.trim()) return;
    try {
      await axios.post(`${API}/client-portal/transactions/${transaction.id}/report-variance`, {
        note, items,
      });
      toast({ title: "Variance reported", description: "Your report has been submitted and we will follow up." });
      setReportVarianceDialog(d => ({ ...d, open: false }));
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      toast({ title: "Could not submit variance", description: msg || "Please try again.", variant: "destructive" });
    }
  };

  const handleDownloadPDF = async (txId, challanNo) => {
    try {
      const res = await axios.get(`${API}/client-portal/transactions/${txId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Challan-${challanNo}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Download failed", variant: "destructive" });
    }
  };

  const splReady = !!(reportDateRange.start_date && reportDateRange.end_date && selectedContractId && selectedContractId !== "all");

  const handleDownloadSummaryPriceList = async () => {
    try {
      const params = new URLSearchParams();
      params.append("contract_id", selectedContractId);
      params.append("start_date", reportDateRange.start_date);
      params.append("end_date", reportDateRange.end_date);
      const selectedContract = clientContracts.find(c => c.id === selectedContractId);
      if (selectedContract?.gst_rate) params.append("gst_rate", selectedContract.gst_rate);
      if (selectedLocation !== "all") params.append("location", selectedLocation);
      const res = await axios.get(`${API}/client-portal/summary-price-list?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Summary-Price-List-${reportDateRange.start_date}-to-${reportDateRange.end_date}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Report failed", variant: "destructive" });
    }
  };

  const handleDownloadCleanDeliveredReport = async () => {
    try {
      const params = new URLSearchParams();
      if (reportDateRange.start_date) params.append("start_date", reportDateRange.start_date);
      if (reportDateRange.end_date) params.append("end_date", reportDateRange.end_date);
      if (selectedContractId && selectedContractId !== "all") params.append("contract_id", selectedContractId);
      if (selectedLocation !== "all") params.append("location", selectedLocation);
      const res = await axios.get(`${API}/client-portal/clean-delivered-report?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "Clean-Delivered-Report.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Report failed", variant: "destructive" });
    }
  };

  const getStatusBadge = (status, approvalState) => {
    if (approvalState === "CONFIRMED") {
      return <Badge variant="outline" className="text-xs border-green-500 text-green-600 dark:text-green-400">Confirmed</Badge>;
    }
    if (approvalState === "DISCREPANCY_RAISED") {
      return <Badge variant="outline" className="text-xs border-red-400 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20">Variance reported</Badge>;
    }
    const map = {
      pending_approval: { label: "Awaiting sign-off", cls: "border-[hsl(var(--klaro-clarity-blue))] text-[hsl(var(--klaro-clarity-blue))]" },
      approved: { label: "Confirmed", cls: "border-green-500 text-green-600 dark:text-green-400" },
      discrepancy: { label: "Needs review", cls: "border-amber-400 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20" },
      discrepancy_raised: { label: "Variance reported", cls: "border-red-400 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20" },
      confirmed: { label: "Confirmed", cls: "border-green-500 text-green-600 dark:text-green-400" },
    };
    const s = map[status] || { label: status, cls: "border-muted-foreground text-muted-foreground" };
    return <Badge variant="outline" className={`text-xs ${s.cls}`}>{s.label}</Badge>;
  };

  const clientName = summary?.portal_display_name || summary?.client_name || user?.name || "Client Portal";
  const clientLogo = summary?.client_logo;

  const SummaryCard = ({ label, value, sub, accentVar, onClick }) => (
    <div
      className={`bg-card border border-border rounded-xl p-4 flex flex-col gap-1 ${onClick ? "cursor-pointer hover:bg-muted/50 transition-colors" : ""}`}
      onClick={onClick}
    >
      <p className="text-xs text-muted-foreground font-medium">{label}</p>
      <p className="text-2xl font-bold leading-tight" style={accentVar ? { color: `hsl(var(${accentVar}))` } : {}}>
        {value ?? "—"}
      </p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground text-sm">Loading your portal…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* ── Header ── */}
      <header className="sticky top-0 z-30 px-4 py-3" style={{ backgroundColor: "hsl(var(--klaro-deep-blue))" }}>
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {clientLogo ? (
              <img src={clientLogo} alt={clientName} className="h-9 w-auto object-contain flex-shrink-0 rounded" />
            ) : (
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                style={{ backgroundColor: "hsl(var(--klaro-clarity-blue))" }}
              >
                {clientName.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="font-semibold text-white text-sm leading-tight truncate">{clientName}</p>
              <p className="text-[10px] leading-tight truncate" style={{ color: "hsl(var(--primary-foreground) / 0.5)" }}>
                {user?.name}{user?.locations?.length ? ` · ${user.locations.slice(0, 2).join(", ")}` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {pendingCount > 0 && (
              <button
                onClick={() => { setStatusFilter("all"); setActiveTab("transactions"); }}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
                style={{ backgroundColor: "hsl(var(--klaro-clarity-blue))", color: "hsl(var(--primary-foreground))" }}
              >
                {pendingCount} pending
              </button>
            )}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg transition-colors"
              style={{ color: "hsl(var(--primary-foreground) / 0.55)" }}
              onMouseEnter={e => e.currentTarget.style.color = "hsl(var(--primary-foreground))"}
              onMouseLeave={e => e.currentTarget.style.color = "hsl(var(--primary-foreground) / 0.55)"}
            >
              {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button
              onClick={() => setShowProfile(true)}
              className="p-2 rounded-lg transition-colors"
              style={{ color: "hsl(var(--primary-foreground) / 0.55)" }}
              onMouseEnter={e => e.currentTarget.style.color = "hsl(var(--primary-foreground))"}
              onMouseLeave={e => e.currentTarget.style.color = "hsl(var(--primary-foreground) / 0.55)"}
            >
              <User className="h-4 w-4" />
            </button>
            <button
              onClick={logout}
              className="p-2 rounded-lg transition-colors"
              style={{ color: "hsl(var(--primary-foreground) / 0.55)" }}
              onMouseEnter={e => e.currentTarget.style.color = "hsl(var(--primary-foreground))"}
              onMouseLeave={e => e.currentTarget.style.color = "hsl(var(--primary-foreground) / 0.55)"}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* ── KPI Strip (compact, scrolls with page) ── */}
      {summary && (
        <div className="max-w-3xl mx-auto w-full px-4 pt-4 pb-2 space-y-3">
            {/* Pending sign-off */}
            {summary.pending_approvals > 0 ? (
              <div
                className="rounded-xl border px-4 py-3 flex items-center justify-between cursor-pointer"
                style={{ borderColor: "hsl(var(--klaro-clarity-blue))", backgroundColor: "hsl(var(--klaro-clarity-blue) / 0.08)" }}
                onClick={() => { setStatusFilter("pending_approval"); setActiveTab("transactions"); }}
              >
                <div>
                  <p className="text-sm font-semibold" style={{ color: "hsl(var(--klaro-clarity-blue))" }}>
                    {summary.pending_approvals} challan{summary.pending_approvals !== 1 ? "s" : ""} awaiting sign-off
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">Tap to review and confirm</p>
                </div>
                <ChevronRight className="h-4 w-4 flex-shrink-0" style={{ color: "hsl(var(--klaro-clarity-blue))" }} />
              </div>
            ) : (
              <div className="rounded-xl border border-border px-4 py-2.5 flex items-center gap-2.5">
                <div className="h-2 w-2 rounded-full bg-green-500 flex-shrink-0" />
                <p className="text-xs text-muted-foreground">No challans pending sign-off</p>
              </div>
            )}

            {/* State cards — current inventory, no date filter */}
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide px-0.5 text-muted-foreground">
                Current inventory
              </p>
              <div className="grid grid-cols-2 gap-3">
                {/* Phase A — baseline-aware clean shelf stock replaces old "In field".
                    Old value used _compute_balance which subtracted PICKUP from FIELD_STORE
                    and could return negative numbers. This card is always ≥ 0. */}
                <SummaryCard
                  label="On shelf"
                  value={
                    shelfStock == null ? "—" :
                    !shelfStock.has_baselines ? "—" :
                    shelfStock.total_shelf_qty
                  }
                  sub={
                    shelfStock == null ? "loading…" :
                    !shelfStock.has_baselines ? "no baseline — contact admin" :
                    shelfStock.any_reconciliation_issue ? "⚠ some items need reconciliation" :
                    "clean linen on your shelves"
                  }
                  accentVar={
                    shelfStock?.has_baselines && !shelfStock?.any_reconciliation_issue
                      ? "--klaro-blue"
                      : undefined
                  }
                />
                <SummaryCard
                  label="In laundry"
                  value={summary.in_laundry ?? summary.soiled_in_wash}
                  sub="at the facility"
                />
              </div>
              {/* Per-location breakdown — only when baselines exist */}
              {shelfStock?.has_baselines && (shelfStock.locations || []).length > 0 && (
                <div className="rounded-lg border border-border bg-muted/30 p-2.5 space-y-1">
                  <p className="text-[9px] uppercase tracking-wide font-semibold text-muted-foreground mb-1">
                    Shelf stock by location
                  </p>
                  {shelfStock.locations.map(loc => {
                    const locTotal = (loc.items || []).reduce((s, i) => s + (i.qty || 0), 0);
                    const hasIssue = loc.has_reconciliation_issue;
                    return (
                      <div key={loc.location_id} className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground truncate max-w-[60%]">{loc.location_id}</span>
                        {hasIssue ? (
                          <span className="text-amber-600 font-medium text-[10px]">⚠ reconciliation needed</span>
                        ) : (
                          <span className="font-semibold tabular-nums">{locTotal.toLocaleString("en-IN")}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Flow cards — date-filter driven */}
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide px-0.5 text-muted-foreground">
                {getTimeframeLabel()}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <SummaryCard
                  label="Picked up"
                  value={summary.picked_up ?? 0}
                  sub="collected soiled"
                />
                <SummaryCard
                  label="Clean delivered"
                  value={summary.clean_delivered ?? summary.clean_delivered_total ?? 0}
                  sub="returned clean"
                  accentVar="--klaro-clarity-blue"
                />
                <SummaryCard
                  label="Discarded"
                  value={summary.discarded ?? 0}
                  sub="condemned items"
                />
                <SummaryCard
                  label="In circulation"
                  value={summary.new_issued ?? 0}
                  sub="new items issued"
                />
              </div>
            </div>
        </div>
      )}

      {/* ── Tabs (with sticky sub-header) ── */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1">
        {/* ── Sticky sub-header: timeframe filter + tab row ── */}
        <div className="sticky top-[60px] z-20 border-b border-border" style={{ backgroundColor: "hsl(var(--background))" }}>
          <div className="max-w-3xl mx-auto w-full px-4 pt-2 pb-2 space-y-2">
            {/* ── Timeframe filter — only for data tabs ── */}
            {activeTab !== "dispatch" && activeTab !== "loadflight" && activeTab !== "transfers" && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 scrollbar-none">
                {[
                  { key: "this_month", label: "This month" },
                  { key: "last_month", label: "Last month" },
                  { key: "last_90_days", label: "Last 90 days" },
                  { key: "all_time", label: "All time" },
                  { key: "custom", label: "Custom" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => { setTimeframe(key); }}
                    className="flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors border"
                    style={
                      timeframe === key
                        ? { backgroundColor: "hsl(var(--klaro-clarity-blue))", color: "hsl(var(--primary-foreground))", borderColor: "hsl(var(--klaro-clarity-blue))" }
                        : { borderColor: "hsl(var(--border))", color: "hsl(var(--muted-foreground))", backgroundColor: "transparent" }
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
              {timeframe === "custom" && (
                <div className="flex items-center gap-2">
                  <KlaroDateInput value={customFrom} onChange={setCustomFrom} />
                  <span className="text-xs text-muted-foreground flex-shrink-0">to</span>
                  <KlaroDateInput value={customTo} onChange={setCustomTo} />
                  <button
                    onClick={fetchData}
                    className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium text-white"
                    style={{ backgroundColor: "hsl(var(--klaro-clarity-blue))" }}
                  >
                    Apply
                  </button>
                </div>
              )}
              {allLocations.length > 1 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground flex-shrink-0">Location:</span>
                  <select
                    value={selectedLocation}
                    onChange={e => setSelectedLocation(e.target.value)}
                    className="text-xs rounded-lg border px-2 py-1 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500"
                    style={{ borderColor: "hsl(var(--border))" }}
                  >
                    <option value="all">All locations</option>
                    {allLocations.map(loc => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
            {/* ── Tab row — horizontally scrollable on mobile ── */}
            <TabsList className="flex overflow-x-auto scrollbar-none gap-0.5 w-full h-auto bg-muted/60 p-1 rounded-xl">
              <TabsTrigger value="transactions" className="flex-shrink-0 text-sm">Transactions</TabsTrigger>
              <TabsTrigger value="balances" className="flex-shrink-0 text-sm">Balances</TabsTrigger>
              {capabilities?.flight_operations && (
                <TabsTrigger value="loadflight" className="flex-shrink-0 text-sm">Load Flight</TabsTrigger>
              )}
              {capabilities?.transfers && (
                <TabsTrigger value="transfers" className="flex-shrink-0 text-sm">Transfers</TabsTrigger>
              )}
              <TabsTrigger value="reports" className="flex-shrink-0 text-sm">Reports</TabsTrigger>
            </TabsList>
          </div>
        </div>

        {/* ── Tab content ── */}
        <div className="flex-1 max-w-3xl mx-auto w-full px-4 py-4">

          {/* ── Transactions Tab ── */}
          <TabsContent value="transactions" className="space-y-3">
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All transactions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All transactions</SelectItem>
                  <SelectItem value="pending_approval">Awaiting sign-off</SelectItem>
                  <SelectItem value="approved">Confirmed</SelectItem>
                  <SelectItem value="discrepancy">Needs review</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchData}>Refresh</Button>
            </div>
            {transactions.length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center text-muted-foreground text-sm">
                  No transactions found.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-1.5">
                {transactions.map((tx, idx) => (
                  <CollapsibleTransactionCard
                    key={tx.id}
                    tx={tx}
                    defaultOpen={idx === 0}
                    getStatusBadge={(s) => getStatusBadge(s, tx.approval_state)}
                    onView={() => setViewTransaction(tx)}
                    onDownloadPDF={() => handleDownloadPDF(tx.id, tx.challan_no)}
                    onApprove={() => { setVariancePreview(null); setApprovalDialog({ open: true, transaction: tx, approved: true, note: "", withSignature: false }); }}
                    onReportIssue={() => setApprovalDialog({ open: true, transaction: tx, approved: false, note: "", withSignature: false })}
                    onReportVariance={() => {
                      const skuItems = (tx.items || [])
                        .map(i => ({ sku: i.item_name || i.name || "", qty: 0 }))
                        .filter(i => i.sku);
                      setReportVarianceDialog({ open: true, transaction: tx, note: "", items: skuItems });
                    }}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── Balances Tab ── */}
          <TabsContent value="balances" className="mt-4 space-y-3">
            <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground font-medium">Ledger view — </strong>
              Opening and closing balances are computed from the movement ledger for the selected period.
              Items in the wash cycle appear as a positive difference between picked-up and delivered counts.
              Contact your account manager if any figure looks unexpected.
            </div>
            {Object.keys(ledgerData).length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center text-muted-foreground text-sm">
                  No balance data available for this period.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {Object.entries(ledgerData).map(([location, items]) => (
                  <Card key={location}>
                    <CardHeader className="py-3 px-4">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <MapPin className="h-4 w-4" style={{ color: "hsl(var(--klaro-clarity-blue))" }} />
                        {location}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                      <div className="overflow-x-auto">
                        <table className="min-w-max w-full text-xs">
                          <thead>
                            <tr className="text-muted-foreground border-b border-border">
                              <th className="text-left py-1.5 pr-3 font-medium">Item</th>
                              <th className="text-right py-1.5 px-2 font-medium">Opening</th>
                              <th className="text-right py-1.5 px-2 font-medium">Picked up</th>
                              <th className="text-right py-1.5 px-2 font-medium">Delivered</th>
                              <th className="text-right py-1.5 px-2 font-medium">New</th>
                              <th className="text-right py-1.5 px-2 font-medium">Discard</th>
                              <th className="text-right py-1.5 px-2 font-medium">Closing</th>
                              <th className="text-right py-1.5 px-2 font-medium">Par</th>
                              <th className="text-right py-1.5 pl-2 font-medium">Shelf</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(items).map(([itemName, row]) => {
                              const pInfo = getParInfo(location, itemName);
                              const parQty = pInfo ? pInfo.authoritative_qty : null;
                              const belowPar = pInfo ? pInfo.below_par : false;
                              return (
                              <tr key={itemName} className="border-b dark:border-slate-800 last:border-0">
                                <td className="py-1.5 pr-3 font-medium dark:text-slate-200">{itemName}</td>
                                <td className="text-right py-1.5 px-2 text-muted-foreground">{row.opening}</td>
                                <td className="text-right py-1.5 px-2 text-muted-foreground">{row.picked_up}</td>
                                <td className="text-right py-1.5 px-2 text-muted-foreground">{row.delivered}</td>
                                <td className="text-right py-1.5 px-2 text-muted-foreground">{row.new_issued || 0}</td>
                                <td className="text-right py-1.5 px-2 text-muted-foreground">{row.discarded || 0}</td>
                                <td className={`text-right py-1.5 px-2 font-semibold ${
                                  (row.closing - row.opening) > 0 ? "text-amber-600 dark:text-amber-400" :
                                  row.closing === 0 ? "text-green-600 dark:text-green-400" : ""
                                }`}>
                                  {row.closing}
                                </td>
                                <td className="text-right py-1.5 px-2">
                                  <button
                                    onClick={() => handleOpenParDialog(location, itemName)}
                                    className="inline-flex items-center gap-1.5 text-xs group"
                                    title="View & set par level"
                                  >
                                    {parQty != null ? (
                                      <span className="inline-flex items-center gap-1">
                                        <span className={`font-semibold ${belowPar ? "text-amber-600 dark:text-amber-400" : "text-foreground dark:text-slate-200"}`}>
                                          {parQty}
                                        </span>
                                        {pInfo && pInfo.mode === "override" ? (
                                          <span className="px-1 py-0.5 rounded text-[9px] font-medium bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300">Set</span>
                                        ) : (
                                          <span className="px-1 py-0.5 rounded text-[9px] font-medium bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300">Auto</span>
                                        )}
                                      </span>
                                    ) : (
                                      <span className="text-muted-foreground/50">—</span>
                                    )}
                                    <Target className="h-2.5 w-2.5 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors flex-shrink-0" />
                                  </button>
                                  {belowPar && pInfo && pInfo.shortfall_qty > 0 && (
                                    <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
                                      short {pInfo.shortfall_qty}
                                      {""}
                                    </div>
                                  )}
                                </td>
                                <td className="text-right py-1.5 pl-2 text-muted-foreground">
                                  {shelfStock && shelfStock[location] && shelfStock[location][itemName] != null
                                    ? shelfStock[location][itemName]
                                    : <span className="text-[10px] italic">not set</span>}
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Variance & Loss section removed in Phase 4 — admin-only */}
            {false && (
              <div className="mt-5 space-y-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <h3 className="text-sm font-semibold text-foreground">Stock Variance</h3>
                </div>

                {/* Residual bucket */}
                <div className={`rounded-lg border px-4 py-3 text-xs leading-relaxed ${
                  lossVariance.residual_bucket.total_residual > 0
                    ? "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30"
                    : "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30"
                }`}>
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${
                      lossVariance.residual_bucket.total_residual > 0
                        ? "text-red-700 dark:text-red-400"
                        : "text-green-700 dark:text-green-400"
                    }`}>
                      {lossVariance.residual_bucket.label}
                    </span>
                    <span className={`font-bold text-sm ${
                      lossVariance.residual_bucket.total_residual > 0
                        ? "text-red-700 dark:text-red-400"
                        : "text-green-700 dark:text-green-400"
                    }`}>
                      {lossVariance.residual_bucket.total_residual > 0
                        ? `${lossVariance.residual_bucket.total_residual} items`
                        : "✓ 0 items unlocated"}
                    </span>
                  </div>
                  <div className="mt-1 text-muted-foreground">
                    Total issued: {lossVariance.residual_bucket.total_pool} &nbsp;·&nbsp;
                    Accounted for: {lossVariance.residual_bucket.total_observable}
                  </div>
                </div>

                {/* Boundary summary */}
                <Card>
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Laundry Boundary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-3 space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Received at wash</span>
                      <span className="font-medium">{lossVariance.boundary_summary.total_dock_received}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Returned clean</span>
                      <span className="font-medium">{lossVariance.boundary_summary.total_dock_returned}</span>
                    </div>
                    <div className={`flex justify-between pt-1 border-t border-border font-semibold ${
                      lossVariance.boundary_summary.dock_cleared
                        ? "text-green-600 dark:text-green-400"
                        : "text-amber-600 dark:text-amber-400"
                    }`}>
                      <span>Balance</span>
                      <span>
                        {lossVariance.boundary_summary.dock_cleared
                          ? "✓ Cleared"
                          : `${lossVariance.boundary_summary.total_dock_delta} outstanding`}
                      </span>
                    </div>
                    <p className="text-muted-foreground pt-1 italic">
                      {lossVariance.boundary_summary.attribution_note}
                    </p>
                  </CardContent>
                </Card>

                {/* Per-SKU variance table — all SKUs from ledger; gap only non-zero when stock-takes recorded */}
                {lossVariance.skus && lossVariance.skus.length > 0 && (
                  <Card>
                    <CardHeader className="py-3 px-4">
                      <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Per-Item Variance
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-muted-foreground border-b border-border">
                              <th className="text-left py-1.5 font-medium">Item</th>
                              <th className="text-right py-1.5 font-medium">Expected</th>
                              <th className="text-right py-1.5 font-medium">Actual</th>
                              <th className="text-right py-1.5 font-medium">Gap</th>
                              <th className="text-right py-1.5 font-medium">₹ Loss</th>
                              <th className="text-right py-1.5 font-medium">Attribution</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lossVariance.skus.map((row, idx) => (
                              <tr key={idx} className="border-b dark:border-slate-800 last:border-0">
                                <td className="py-1.5 font-medium dark:text-slate-200">
                                  {row.sku_id}
                                  {row.location_id && (
                                    <span className="ml-1 text-muted-foreground font-normal">({row.location_id})</span>
                                  )}
                                </td>
                                <td className="text-right py-1.5 text-muted-foreground">{row.expected_qty}</td>
                                <td className="text-right py-1.5 text-muted-foreground">{row.actual_qty}</td>
                                <td className={`text-right py-1.5 font-semibold ${
                                  row.gap_qty < 0
                                    ? "text-red-600 dark:text-red-400"
                                    : row.gap_qty > 0
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-muted-foreground"
                                }`}>
                                  {row.gap_qty < 0 ? `−${Math.abs(row.gap_qty)}` : row.gap_qty > 0 ? `+${row.gap_qty}` : "—"}
                                </td>
                                <td className={`text-right py-1.5 ${row.rupee_loss > 0 ? "text-red-600 dark:text-red-400 font-semibold" : "text-muted-foreground"}`}>
                                  {row.rupee_loss > 0 ? `₹${row.rupee_loss.toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—"}
                                </td>
                                <td className={`text-right py-1.5 ${
                                  row.attribution === "laundry_dock" ? "text-amber-600 dark:text-amber-400" :
                                  row.attribution === "field" ? "text-red-600 dark:text-red-400" :
                                  "text-muted-foreground"
                                }`}>
                                  {row.attribution === "laundry_dock" ? "Dock" : row.attribution === "field" ? "Field" : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* ── Reports Tab ── */}
          <TabsContent value="reports" className="mt-4 space-y-4">
            {/* Intro */}
            <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground font-medium">Your reports — </strong>
              Download PDF reports covering your linen service. Select a date range and, where needed, a specific contract to generate each report.
            </div>

            {/* Shared filters */}
            <div className={`grid gap-3 ${clientContracts.length > 0 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}>
              <div>
                <label className="text-xs font-medium block mb-1.5" style={{ color: "hsl(var(--klaro-deep-blue))" }}>
                  From date
                </label>
                <KlaroDateInput
                  value={reportDateRange.start_date}
                  onChange={v => setReportDateRange(r => ({ ...r, start_date: v }))}
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1.5" style={{ color: "hsl(var(--klaro-deep-blue))" }}>
                  To date
                </label>
                <KlaroDateInput
                  value={reportDateRange.end_date}
                  onChange={v => setReportDateRange(r => ({ ...r, end_date: v }))}
                />
              </div>
              {clientContracts.length > 0 && (
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: "hsl(var(--klaro-deep-blue))" }}>
                    Contract
                  </label>
                  <Select value={selectedContractId} onValueChange={setSelectedContractId}>
                    <SelectTrigger><SelectValue placeholder="All contracts" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All contracts</SelectItem>
                      {clientContracts.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.contract_id || c.id}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Report cards */}
            <div className="space-y-3">

              {/* 1 — Clean Delivered Report */}
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "hsl(var(--klaro-mist))" }}
                  >
                    <Download className="h-4 w-4" style={{ color: "hsl(var(--klaro-blue))" }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: "hsl(var(--klaro-deep-blue))" }}>
                      Clean Delivered Report
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                      All clean linen returned to your locations in the selected period — item counts, challan numbers, and dates. A start date is required; end date is optional.
                    </p>
                  </div>
                </div>
                <button
                  className="w-full py-2.5 px-4 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                  style={{
                    backgroundColor: reportDateRange.start_date ? "hsl(var(--klaro-blue))" : "hsl(var(--klaro-mist))",
                    color: reportDateRange.start_date ? "hsl(var(--primary-foreground))" : "hsl(var(--klaro-slate))",
                    cursor: reportDateRange.start_date ? "pointer" : "not-allowed",
                    opacity: reportDateRange.start_date ? 1 : 0.75,
                  }}
                  disabled={!reportDateRange.start_date}
                  onClick={handleDownloadCleanDeliveredReport}
                  onMouseEnter={e => { if (reportDateRange.start_date) e.currentTarget.style.backgroundColor = "hsl(var(--klaro-deep-blue))"; }}
                  onMouseLeave={e => { if (reportDateRange.start_date) e.currentTarget.style.backgroundColor = "hsl(var(--klaro-blue))"; }}
                >
                  <Download className="h-4 w-4" />
                  {reportDateRange.start_date ? "Download PDF" : "Select a start date to download"}
                </button>
              </div>

              {/* 2 — Summary & Price List */}
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "hsl(var(--klaro-mist))" }}
                  >
                    <FileText className="h-4 w-4" style={{ color: "hsl(var(--klaro-blue))" }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: "hsl(var(--klaro-deep-blue))" }}>
                      Summary & Price List
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                      Itemised delivery totals with unit rates, amounts, and GST breakdown. Both dates and a specific contract are required.
                    </p>
                  </div>
                </div>
                {clientContracts.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-1">
                    No contracts found for your account. Contact your account manager.
                  </p>
                ) : (
                  <button
                    className="w-full py-2.5 px-4 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                    style={{
                      backgroundColor: splReady ? "hsl(var(--klaro-blue))" : "hsl(var(--klaro-mist))",
                      color: splReady ? "hsl(var(--primary-foreground))" : "hsl(var(--klaro-slate))",
                      cursor: splReady ? "pointer" : "not-allowed",
                      opacity: splReady ? 1 : 0.75,
                    }}
                    disabled={!splReady}
                    onClick={handleDownloadSummaryPriceList}
                    onMouseEnter={e => { if (splReady) e.currentTarget.style.backgroundColor = "hsl(var(--klaro-deep-blue))"; }}
                    onMouseLeave={e => { if (splReady) e.currentTarget.style.backgroundColor = "hsl(var(--klaro-blue))"; }}
                  >
                    <Download className="h-4 w-4" />
                    {!reportDateRange.start_date || !reportDateRange.end_date
                      ? "Select both dates to continue"
                      : selectedContractId === "all"
                      ? "Select a specific contract to download"
                      : "Download PDF"}
                  </button>
                )}
              </div>

            </div>
          </TabsContent>

          {/* ── Load Flight Tab ── */}
          {capabilities?.flight_operations && (
          <TabsContent value="loadflight" className="mt-4 space-y-4">
            {flightFormData.flights.length === 0 && flightFormData.aircraft_types.length === 0 && !flHistoryLoading ? (
              <Card>
                <CardContent className="py-14 text-center space-y-3">
                  <div
                    className="mx-auto h-12 w-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: "hsl(var(--klaro-mist))" }}
                  >
                    <AlertCircle className="h-5 w-5" style={{ color: "hsl(var(--klaro-slate))" }} />
                  </div>
                  <h3 className="font-semibold text-sm">No flights set up yet</h3>
                  <p className="text-xs text-muted-foreground max-w-xs mx-auto leading-relaxed">
                    Your flights and aircraft templates need to be added before you can record loadings.
                    Please contact Kalsi to get set up.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* ── Loading form ── */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Record a flight loading</CardTitle>
                    <CardDescription className="text-xs">
                      Pick the flight and aircraft — we'll pre-fill the usual quantities. Change any number
                      to match what you actually loaded, then confirm.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {flightFormData.locations.length > 1 && (
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">Location</label>
                        <select
                          value={flLocation}
                          onChange={e => setFlLocation(e.target.value)}
                          className="w-full text-sm rounded-lg border px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500"
                          style={{ borderColor: "hsl(var(--border))" }}
                        >
                          <option value="">Select location…</option>
                          {flightFormData.locations.map(l => (
                            <option key={l} value={l}>{l}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">Flight</label>
                        <select
                          value={flFlightId}
                          onChange={e => handleSelectFlight(e.target.value)}
                          className="w-full text-sm rounded-lg border px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500"
                          style={{ borderColor: "hsl(var(--border))" }}
                        >
                          <option value="">Select flight…</option>
                          {flightFormData.flights.map(f => (
                            <option key={f.id} value={f.id}>
                              {f.flight_designator}{f.route ? ` · ${f.route}` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">Flight date</label>
                        <KlaroDateInput value={flDate} onChange={setFlDate} />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Aircraft type</label>
                      <select
                        value={flAircraftTypeId}
                        onChange={e => applyAircraftTemplate(e.target.value)}
                        className="w-full text-sm rounded-lg border px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500"
                        style={{ borderColor: "hsl(var(--border))" }}
                      >
                        <option value="">Select aircraft…</option>
                        {flightFormData.aircraft_types.map(a => (
                          <option key={a.id} value={a.id}>
                            {a.name}{a.classification ? ` · ${a.classification}` : ""}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedAircraftType && (
                      <div className="rounded-lg border border-border overflow-hidden">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-muted text-muted-foreground border-b border-border">
                              <th className="text-left py-2 px-3 font-medium">Item</th>
                              <th className="text-right py-2 px-3 font-medium">Quantity loaded</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedAircraftType.items || []).length === 0 ? (
                              <tr>
                                <td colSpan={2} className="py-4 px-3 text-center text-muted-foreground">
                                  This aircraft has no template items. Add items to it first.
                                </td>
                              </tr>
                            ) : (
                              (selectedAircraftType.items || []).map(it => (
                                <tr key={it.sku_id} className="border-b border-border last:border-0">
                                  <td className="py-1.5 px-3 font-medium dark:text-slate-200">{it.sku_id}</td>
                                  <td className="py-1.5 px-3 text-right">
                                    <Input
                                      type="number"
                                      min="0"
                                      inputMode="numeric"
                                      value={flQtys[it.sku_id] ?? ""}
                                      onChange={e => setFlQtys(q => ({ ...q, [it.sku_id]: e.target.value }))}
                                      className="h-8 w-24 ml-auto text-right text-sm"
                                    />
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <Button
                      onClick={handleSubmitFlightLoading}
                      disabled={flSubmitting || !selectedAircraftType}
                      className="w-full"
                      style={{ backgroundColor: "hsl(var(--klaro-clarity-blue))" }}
                    >
                      {flSubmitting ? "Recording…" : "Confirm loading"}
                    </Button>
                  </CardContent>
                </Card>

                {/* ── Recent loadings ── */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm">Recent loadings</h3>
                    <Button variant="outline" size="sm" onClick={fetchFlightLoadingData}>Refresh</Button>
                  </div>
                  {flLoadings.length === 0 ? (
                    <Card>
                      <CardContent className="py-10 text-center text-muted-foreground text-sm">
                        {flHistoryLoading ? "Loading…" : "No flight loadings yet."}
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      {flLoadings.map(loading => (
                        <Card key={loading.challan_ref}>
                          <CardContent className="py-3 space-y-2">
                            <div className="flex items-center justify-between flex-wrap gap-1">
                              <div>
                                <p className="font-semibold text-sm dark:text-white">
                                  {loading.flight_designator || "Flight"}
                                  {loading.aircraft_type_name ? ` · ${loading.aircraft_type_name}` : ""}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {loading.flight_date ? formatDate(loading.flight_date) : ""}
                                  {loading.location_id ? ` · ${loading.location_id}` : ""}
                                </p>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {loading.total_net_qty} loaded
                              </Badge>
                            </div>
                            <div className="rounded-lg border border-border overflow-hidden">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-muted text-muted-foreground border-b border-border">
                                    <th className="text-left py-1.5 px-3 font-medium">Item</th>
                                    <th className="text-right py-1.5 px-3 font-medium">Loaded</th>
                                    <th className="text-right py-1.5 px-3 font-medium"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {loading.lines.map(line => (
                                    <tr key={line.movement_id} className="border-b border-border last:border-0">
                                      <td className="py-1.5 px-3 font-medium dark:text-slate-200">{line.sku_id}</td>
                                      <td className="py-1.5 px-3 text-right text-muted-foreground">
                                        {line.net_qty}
                                        {line.net_qty !== line.loaded_qty && (
                                          <span className="text-[10px] ml-1">(was {line.loaded_qty})</span>
                                        )}
                                      </td>
                                      <td className="py-1.5 px-3 text-right">
                                        <button
                                          onClick={() => setCorrectionDialog({
                                            open: true, line, loading, value: String(line.net_qty), saving: false,
                                          })}
                                          className="text-[11px] font-medium underline"
                                          style={{ color: "hsl(var(--klaro-clarity-blue))" }}
                                        >
                                          Correct
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </TabsContent>
          )}

          {/* ── Transfers Tab ── */}
          {capabilities?.transfers && (
          <TabsContent value="transfers" className="mt-4 space-y-4">
            {transferFormData.locations.length < 2 && !trHistoryLoading && transfers.length === 0 ? (
              <Card>
                <CardContent className="py-14 text-center space-y-3">
                  <div
                    className="mx-auto h-12 w-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: "hsl(var(--klaro-mist))" }}
                  >
                    <ArrowLeftRight className="h-5 w-5" style={{ color: "hsl(var(--klaro-slate))" }} />
                  </div>
                  <h3 className="font-semibold text-sm">Transfers need at least two locations</h3>
                  <p className="text-xs text-muted-foreground max-w-xs mx-auto leading-relaxed">
                    Once you have stock at more than one location, you can move it between them here.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* ── New transfer form ── */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Send a transfer</CardTitle>
                    <CardDescription className="text-xs">
                      Move clean stock from one location to another. It leaves the shelf right away and
                      shows as "in transit" until the other location confirms what they received.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">From location</label>
                        <select
                          value={trFrom}
                          onChange={e => { setTrFrom(e.target.value); setTrQtys({}); }}
                          className="w-full text-sm rounded-lg border px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500"
                          style={{ borderColor: "hsl(var(--border))" }}
                        >
                          <option value="">Select location…</option>
                          {transferFormData.locations.map(l => (
                            <option key={l} value={l}>{l}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">To location</label>
                        <select
                          value={trTo}
                          onChange={e => setTrTo(e.target.value)}
                          className="w-full text-sm rounded-lg border px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500"
                          style={{ borderColor: "hsl(var(--border))" }}
                        >
                          <option value="">Select location…</option>
                          {transferFormData.locations.filter(l => l !== trFrom).map(l => (
                            <option key={l} value={l}>{l}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {trFrom && (
                      <div className="rounded-lg border border-border overflow-hidden">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-muted text-muted-foreground border-b border-border">
                              <th className="text-left py-2 px-3 font-medium">Item</th>
                              <th className="text-right py-2 px-3 font-medium">On shelf</th>
                              <th className="text-right py-2 px-3 font-medium">Send</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trSourceStock.length === 0 ? (
                              <tr>
                                <td colSpan={3} className="py-4 px-3 text-center text-muted-foreground">
                                  No stock on the shelf at this location.
                                </td>
                              </tr>
                            ) : (
                              trSourceStock.map(it => (
                                <tr key={it.sku_id} className="border-b border-border last:border-0">
                                  <td className="py-1.5 px-3 font-medium dark:text-slate-200">{it.sku_id}</td>
                                  <td className="py-1.5 px-3 text-right text-muted-foreground">{it.available_qty}</td>
                                  <td className="py-1.5 px-3 text-right">
                                    <Input
                                      type="number"
                                      min="0"
                                      max={it.available_qty}
                                      inputMode="numeric"
                                      value={trQtys[it.sku_id] ?? ""}
                                      onChange={e => setTrQtys(q => ({ ...q, [it.sku_id]: e.target.value }))}
                                      className="h-8 w-24 ml-auto text-right text-sm"
                                    />
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <Button
                      onClick={handleSubmitTransfer}
                      disabled={trSubmitting || !trFrom || !trTo || trSourceStock.length === 0}
                      className="w-full"
                      style={{ backgroundColor: "hsl(var(--klaro-clarity-blue))" }}
                    >
                      {trSubmitting ? "Sending…" : "Send transfer"}
                    </Button>
                  </CardContent>
                </Card>

                {/* ── Transfer history ── */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm">Transfers</h3>
                    <Button variant="outline" size="sm" onClick={fetchTransferData}>Refresh</Button>
                  </div>
                  {transfers.length === 0 ? (
                    <Card>
                      <CardContent className="py-10 text-center text-muted-foreground text-sm">
                        {trHistoryLoading ? "Loading…" : "No transfers yet."}
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      {transfers.map(t => {
                        const statusStyle =
                          t.status === "RECEIVED" ? "text-green-700 dark:text-green-400 border-green-300" :
                          t.status === "SHORT" ? "text-amber-700 dark:text-amber-400 border-amber-300" :
                          "text-blue-700 dark:text-blue-400 border-blue-300";
                        const statusLabel =
                          t.status === "RECEIVED" ? "Received" :
                          t.status === "SHORT" ? "Short" : "In transit";
                        return (
                          <Card key={t.transfer_ref}>
                            <CardContent className="py-3 space-y-2">
                              <div className="flex items-center justify-between flex-wrap gap-1">
                                <div>
                                  <p className="font-semibold text-sm dark:text-white flex items-center gap-1.5">
                                    {t.from_location_id}
                                    <ArrowLeftRight className="h-3.5 w-3.5 text-muted-foreground" />
                                    {t.to_location_id}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {t.initiated_at ? formatDate(t.initiated_at) : ""}
                                  </p>
                                </div>
                                <Badge variant="outline" className={`text-xs ${statusStyle}`}>
                                  {statusLabel}
                                </Badge>
                              </div>
                              <div className="rounded-lg border border-border overflow-hidden">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="bg-muted text-muted-foreground border-b border-border">
                                      <th className="text-left py-1.5 px-3 font-medium">Item</th>
                                      <th className="text-right py-1.5 px-3 font-medium">Sent</th>
                                      <th className="text-right py-1.5 px-3 font-medium">Received</th>
                                      <th className="text-right py-1.5 px-3 font-medium">In transit</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {t.lines.map(line => (
                                      <tr key={line.sku_id} className="border-b border-border last:border-0">
                                        <td className="py-1.5 px-3 font-medium dark:text-slate-200">{line.sku_id}</td>
                                        <td className="py-1.5 px-3 text-right text-muted-foreground">{line.sent_qty}</td>
                                        <td className="py-1.5 px-3 text-right text-muted-foreground">{line.received_qty}</td>
                                        <td className={`py-1.5 px-3 text-right font-semibold ${
                                          line.outstanding_qty > 0 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"
                                        }`}>
                                          {line.outstanding_qty}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              {t.total_outstanding_qty > 0 && (
                                <Button
                                  size="sm"
                                  onClick={() => openReceiveDialog(t)}
                                  className="w-full"
                                  style={{ backgroundColor: "hsl(var(--klaro-clarity-blue))" }}
                                >
                                  Confirm receipt
                                </Button>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </TabsContent>
          )}
        </div>
      </Tabs>

      {/* ── Footer ── */}
      <footer className="text-center py-4 text-[10px]" style={{ color: "hsl(var(--klaro-slate))" }}>
        Powered by <strong>Klaro</strong> · serviced by Kalsi Drycleaners &amp; Dyers
      </footer>

      {/* ── View Transaction Dialog ── */}
      {viewTransaction && (
        <Dialog open={!!viewTransaction} onOpenChange={() => setViewTransaction(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-0">
            <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm dark:text-white">Challan {viewTransaction.challan_no}</p>
                <p className="text-xs text-muted-foreground">
                  {viewTransaction.location_name} · {formatDate(viewTransaction.timestamp)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => handleDownloadPDF(viewTransaction.id, viewTransaction.challan_no)}>
                  <Download className="h-3.5 w-3.5 mr-1" /> PDF
                </Button>
                <Button size="sm" variant="outline" onClick={() => setViewTransaction(null)}>Close</Button>
              </div>
            </div>
            <PortalChallanView transaction={viewTransaction} company_name={company_name} logo_base64={logo_base64} tagline={tagline} />
          </DialogContent>
        </Dialog>
      )}

      {/* ── Approval / Flag-for-review Dialog ── */}
      <Dialog open={approvalDialog.open} onOpenChange={o => { if (!o) { setApprovalDialog(d => ({ ...d, open: false })); setVariancePreview(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {approvalDialog.approved ? (variancePreview ? "Review & confirm" : "Confirm this delivery") : "Flag for review"}
            </DialogTitle>
            <DialogDescription>
              {approvalDialog.approved
                ? variancePreview
                  ? "Review the variance below, then confirm."
                  : "Please confirm the linen count matches the challan."
                : "Let us know what looks different — we'll review it together and follow up with you."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {variancePreview && variancePreview.skus?.length > 0 && (
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted text-muted-foreground border-b border-border">
                      <th className="text-left py-2 px-3 font-medium">Item</th>
                      <th className="text-right py-2 px-3 font-medium">Qty</th>
                      <th className="text-right py-2 px-3 font-medium">After delivery</th>
                      <th className="text-right py-2 px-3 font-medium">Par target</th>
                      <th className="text-right py-2 px-3 font-medium">vs par</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variancePreview.skus.map((row, i) => {
                      const hasPar = row.par_qty != null;
                      const vsParVal = row.vs_par;
                      const isShortfall = hasPar && vsParVal != null && vsParVal < 0;
                      const isMet = hasPar && vsParVal != null && vsParVal >= 0;
                      return (
                        <tr key={i} className="border-b border-border last:border-0">
                          <td className="py-1.5 px-3 font-medium dark:text-slate-200">{row.sku}</td>
                          <td className="py-1.5 px-3 text-right text-muted-foreground">{row.agreed_qty}</td>
                          <td className="py-1.5 px-3 text-right text-muted-foreground">
                            {row.balance_after != null ? row.balance_after : "—"}
                          </td>
                          <td className="py-1.5 px-3 text-right text-muted-foreground">
                            {hasPar ? row.par_qty : (row.expected_qty != null ? row.expected_qty : "—")}
                          </td>
                          <td className={`py-1.5 px-3 text-right font-semibold ${
                            isShortfall ? "text-amber-600 dark:text-amber-400" :
                            isMet ? "text-green-600 dark:text-green-400" :
                            row.variance !== 0 ? "text-amber-600 dark:text-amber-400" : "text-green-600 dark:text-green-400"
                          }`}>
                            {hasPar && vsParVal != null
                              ? (vsParVal >= 0 ? `+${vsParVal}` : vsParVal)
                              : (row.variance > 0 ? `+${row.variance}` : row.variance)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {approvalDialog.approved && !variancePreview && user?.signature && (
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <input
                  type="checkbox"
                  id="withSig"
                  checked={approvalDialog.withSignature}
                  onChange={e => setApprovalDialog(d => ({ ...d, withSignature: e.target.checked }))}
                  className="rounded"
                />
                <label htmlFor="withSig" className="text-sm cursor-pointer dark:text-slate-300">
                  Sign with my saved signature
                </label>
              </div>
            )}
            {!approvalDialog.approved && (
              <Textarea
                placeholder="Describe what looks different (count, item, condition…)"
                value={approvalDialog.note}
                onChange={e => setApprovalDialog(d => ({ ...d, note: e.target.value }))}
                rows={3}
              />
            )}
            <div className="flex gap-2">
              <Button
                className={`flex-1 ${approvalDialog.approved ? "bg-green-600 hover:bg-green-700 text-white" : ""}`}
                variant={approvalDialog.approved ? "default" : "outline"}
                onClick={handleApproval}
                disabled={(variancePreviewLoading) || (!approvalDialog.approved && !approvalDialog.note.trim())}
              >
                {variancePreviewLoading
                  ? "Loading…"
                  : approvalDialog.approved
                    ? variancePreview ? "Confirm" : "Next → Review"
                    : "Submit"}
              </Button>
              <Button variant="outline" onClick={() => { setApprovalDialog(d => ({ ...d, open: false })); setVariancePreview(null); }}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Report Variance Dialog (post-confirmation) ── */}
      <Dialog open={reportVarianceDialog.open} onOpenChange={o => !o && setReportVarianceDialog(d => ({ ...d, open: false }))}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Report Variance</DialogTitle>
            <DialogDescription>
              Enter the quantity difference for each item on challan {reportVarianceDialog.transaction?.challan_no}.
              Positive = more than confirmed; negative = fewer. At least one non-zero entry required.
              This creates a new linked record — the original challan is not changed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {reportVarianceDialog.items.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Item</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600 dark:text-slate-300 w-28">Qty Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportVarianceDialog.items.map((item, idx) => (
                      <tr key={idx} className="border-t border-slate-100 dark:border-slate-700">
                        <td className="px-3 py-2 text-slate-700 dark:text-slate-200">{item.sku}</td>
                        <td className="px-3 py-1 text-right">
                          <input
                            type="number"
                            value={item.qty}
                            onChange={e => {
                              const val = parseInt(e.target.value, 10) || 0;
                              setReportVarianceDialog(d => ({
                                ...d,
                                items: d.items.map((it, i) => i === idx ? { ...it, qty: val } : it),
                              }));
                            }}
                            className="w-24 text-right border rounded px-2 py-1 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-blue-400"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">No items found on this transaction.</p>
            )}
            <Textarea
              placeholder="Describe the variance (reason, condition, context…)"
              value={reportVarianceDialog.note}
              onChange={e => setReportVarianceDialog(d => ({ ...d, note: e.target.value }))}
              rows={2}
            />
            <div className="flex gap-2">
              <Button
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                onClick={handleReportVariance}
                disabled={
                  !reportVarianceDialog.note.trim() ||
                  !reportVarianceDialog.items.some(i => i.qty !== 0)
                }
              >
                Submit Report
              </Button>
              <Button variant="outline" onClick={() => setReportVarianceDialog(d => ({ ...d, open: false }))}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ProfileSettings open={showProfile} onClose={() => setShowProfile(false)} />

      {/* ── Par Decision Dialog ── */}
      <Dialog open={parDialog.open} onOpenChange={o => !o && setParDialog(d => ({ ...d, open: false }))}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-4 w-4" style={{ color: "hsl(var(--klaro-clarity-blue))" }} />
              Par level — {parDialog.skuId}
            </DialogTitle>
            <DialogDescription>
              Par is the target stock for this item. We suggest one from your flight schedule — keep it (Auto) or set your own (Set).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground space-y-0.5">
              <div><span className="font-medium text-foreground">Item:</span> {parDialog.skuId}</div>
              <div><span className="font-medium text-foreground">Location:</span> {parDialog.locationId}</div>
            </div>

            {/* Suggested vs current */}
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-blue-600 dark:text-blue-400 font-medium">Suggested</div>
                <div className="text-lg font-bold text-blue-700 dark:text-blue-300">{parDialog.proposed}</div>
                <div className="text-[10px] text-muted-foreground">from flight schedule</div>
              </div>
              <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Current par</div>
                <div className="text-lg font-bold text-foreground">{parDialog.authoritative}</div>
                <div className="text-[10px]">
                  {parDialog.mode === "override"
                    ? <span className="text-slate-500">fixed by you</span>
                    : <span className="text-blue-500">following suggestion</span>}
                </div>
              </div>
            </div>

            {/* Accept (only when not already following engine) */}
            {parDialog.mode !== "engine" && (
              <Button
                className="w-full"
                style={{ backgroundColor: "hsl(var(--klaro-clarity-blue))", color: "hsl(var(--primary-foreground))" }}
                onClick={handleParAccept}
                disabled={parDialog.saving}
              >
                Use suggested ({parDialog.proposed}) &amp; keep auto-adjusting
              </Button>
            )}
            {parDialog.mode === "engine" && (
              <Button className="w-full" variant="outline" onClick={handleParAccept} disabled={parDialog.saving}>
                Refresh to latest suggestion ({parDialog.proposed})
              </Button>
            )}

            {/* Override */}
            <div className="rounded-lg border border-border px-3 py-2 space-y-2">
              <label className="text-xs font-medium block text-foreground">Set your own fixed par</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  value={parDialog.overrideVal}
                  onChange={e => setParDialog(d => ({ ...d, overrideVal: e.target.value }))}
                  placeholder="e.g. 50"
                  className="flex-1 text-sm rounded-lg border px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500"
                  style={{ borderColor: "hsl(var(--border))" }}
                />
                <Button
                  onClick={handleParOverride}
                  disabled={parDialog.saving || !String(parDialog.overrideVal).trim()}
                >
                  {parDialog.saving ? "Saving…" : "Set"}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground">A fixed par stays put until you release it back to the suggestion.</p>
            </div>

            {/* Release */}
            {parDialog.mode === "override" && (
              <Button className="w-full" variant="outline" onClick={handleParRelease} disabled={parDialog.saving}>
                Release back to suggested par
              </Button>
            )}

            {/* History */}
            <div className="pt-1">
              <div className="text-xs font-medium text-foreground mb-1.5">History</div>
              {parDialog.loadingHistory ? (
                <p className="text-xs text-muted-foreground">Loading…</p>
              ) : parDialog.history.length === 0 ? (
                <p className="text-xs text-muted-foreground">No changes yet.</p>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="bg-muted text-muted-foreground border-b border-border">
                        <th className="text-left py-1.5 px-2 font-medium">When</th>
                        <th className="text-right py-1.5 px-2 font-medium">Par</th>
                        <th className="text-left py-1.5 px-2 font-medium">Type</th>
                        <th className="text-left py-1.5 px-2 font-medium">By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parDialog.history.map((h, i) => (
                        <tr key={i} className="border-b border-border last:border-0">
                          <td className="py-1.5 px-2 text-muted-foreground">{formatDate(h.effective_from)}</td>
                          <td className="py-1.5 px-2 text-right font-medium dark:text-slate-200">{h.par_qty}</td>
                          <td className="py-1.5 px-2">
                            {h.mode === "override"
                              ? <span className="text-slate-500">Set</span>
                              : <span className="text-blue-500">Auto</span>}
                          </td>
                          <td className="py-1.5 px-2 text-muted-foreground capitalize">{h.set_by_role || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <Button variant="outline" className="w-full" onClick={() => setParDialog(d => ({ ...d, open: false }))}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Correct Flight Loading Dialog ── */}
      <Dialog
        open={correctionDialog.open}
        onOpenChange={o => !o && setCorrectionDialog({ open: false, line: null, loading: null, value: "", saving: false })}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Correct loaded quantity</DialogTitle>
            <DialogDescription>
              Enter the quantity that should actually have been loaded
              {correctionDialog.line ? ` for ${correctionDialog.line.sku_id}` : ""}.
              {" "}We'll keep the full history and adjust the shelf stock accordingly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {correctionDialog.line && (
              <p className="text-xs text-muted-foreground">
                Currently recorded as loaded: <strong>{correctionDialog.line.net_qty}</strong>
              </p>
            )}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Correct quantity</label>
              <Input
                type="number"
                min="0"
                inputMode="numeric"
                value={correctionDialog.value}
                onChange={e => setCorrectionDialog(d => ({ ...d, value: e.target.value }))}
                className="h-9"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setCorrectionDialog({ open: false, line: null, loading: null, value: "", saving: false })}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitCorrection}
                disabled={correctionDialog.saving}
                style={{ backgroundColor: "hsl(var(--klaro-clarity-blue))" }}
              >
                {correctionDialog.saving ? "Saving…" : "Save correction"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Confirm Transfer Receipt Dialog ── */}
      <Dialog
        open={receiveDialog.open}
        onOpenChange={o => !o && setReceiveDialog({ open: false, transfer: null, qtys: {}, saving: false })}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm what you received</DialogTitle>
            <DialogDescription>
              Enter the actual quantity received at{" "}
              {receiveDialog.transfer ? <strong>{receiveDialog.transfer.to_location_id}</strong> : "this location"}.
              {" "}Anything short of what was sent stays flagged as in transit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {receiveDialog.transfer && (
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted text-muted-foreground border-b border-border">
                      <th className="text-left py-2 px-3 font-medium">Item</th>
                      <th className="text-right py-2 px-3 font-medium">In transit</th>
                      <th className="text-right py-2 px-3 font-medium">Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receiveDialog.transfer.lines
                      .filter(l => l.outstanding_qty > 0)
                      .map(l => (
                        <tr key={l.sku_id} className="border-b border-border last:border-0">
                          <td className="py-1.5 px-3 font-medium dark:text-slate-200">{l.sku_id}</td>
                          <td className="py-1.5 px-3 text-right text-muted-foreground">{l.outstanding_qty}</td>
                          <td className="py-1.5 px-3 text-right">
                            <Input
                              type="number"
                              min="0"
                              max={l.outstanding_qty}
                              inputMode="numeric"
                              value={receiveDialog.qtys[l.sku_id] ?? ""}
                              onChange={e => setReceiveDialog(d => ({ ...d, qtys: { ...d.qtys, [l.sku_id]: e.target.value } }))}
                              className="h-8 w-24 ml-auto text-right text-sm"
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setReceiveDialog({ open: false, transfer: null, qtys: {}, saving: false })}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitReceive}
                disabled={receiveDialog.saving}
                style={{ backgroundColor: "hsl(var(--klaro-clarity-blue))" }}
              >
                {receiveDialog.saving ? "Saving…" : "Confirm receipt"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AppContent = () => {
  const { user, loading, pendingApproval, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground text-sm">Loading…</div>
      </div>
    );
  }

  if (pendingApproval) {
    return <PendingApprovalScreen />;
  }

  if (!user) {
    return (
      <div className="h-screen flex flex-col items-center justify-center p-4 text-center gap-4">
        <p className="text-red-500 text-sm">
          Unable to verify your account. Please sign in again.
        </p>
        <Button onClick={async () => { await logout(); navigate("/", { replace: true }); }}>
          Back to Sign In
        </Button>
      </div>
    );
  }

  if (user.role === "client") {
    // Ensure client users stay on /portal
    if (location.pathname !== "/portal") {
      return <Navigate to="/portal" replace />;
    }
    return <ClientPortal />;
  }

  // Non-client users should not be on /portal
  if (location.pathname === "/portal") {
    return <Navigate to="/dashboard" replace />;
  }

  return <MainApp />;
};

// Map URL path segments to view IDs
const PATH_TO_VIEW = {
  "dashboard": "dashboard",
  "analytics": "analytics",
  "clients": "clients",
  "transactions": "transactions",
  "railway": "railway",
  "railway-coaching": "railway-coaching",
  "admin": "admin",
  "settings": "settings",
  "company-profile": "company-profile",
  "custody": "custody",
};
const VIEW_TO_PATH = Object.fromEntries(Object.entries(PATH_TO_VIEW).map(([k, v]) => [v, k]));
const DEFAULT_VIEW = "dashboard";

const MainApp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { company_name: appName, logo_base64: appLogo } = useCompanyProfile();

  // Derive current view from URL path; fall back to dashboard
  const pathSegment = location.pathname.replace(/^\//, "");
  const currentView = PATH_TO_VIEW[pathSegment] || DEFAULT_VIEW;

  // Admin-only views: block direct URL access for non-admin roles
  const ADMIN_ONLY_VIEWS = ["dashboard", "analytics", "clients", "admin", "settings", "company-profile"];
  if (user?.role === "worker" && currentView !== "custody") {
    return <Navigate to="/custody" replace />;
  }
  if (ADMIN_ONLY_VIEWS.includes(currentView) && user?.role !== "admin") {
    return <Navigate to="/transactions" replace />;
  }

  const setCurrentView = (view) => {
    navigate(`/${VIEW_TO_PATH[view] || view}`, { replace: false });
  };

  const viewLabels = {
    dashboard: "Dashboard",
    analytics: "Analytics",
    clients: "Clients",
    transactions: "Transactions",
    railway: "Railway Challans",
    "railway-coaching": "Railway Coaching",
    admin: "Admin Panel",
    settings: "Settings",
    "company-profile": "Company Profile",
    custody: "Canvas Custody",
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <DesktopSidebar
        currentView={currentView}
        setCurrentView={setCurrentView}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="md:hidden px-4 py-3 bg-card border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {appLogo && (
              <img src={appLogo} alt={appName} className="h-7 w-auto object-contain flex-shrink-0" />
            )}
            <div>
              <h1 className="font-semibold text-base leading-tight" style={{ color: "hsl(var(--klaro-deep-blue))" }}>{appName || 'Klaro by Kalsi'}</h1>
              <p className="text-[10px] text-muted-foreground">
                {viewLabels[currentView]}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <Badge variant="outline" className="text-xs">
              {user?.role}
            </Badge>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-3 md:p-6 pb-20 md:pb-6">
          <Suspense fallback={<div className="flex items-center justify-center h-32 text-muted-foreground text-sm">Loading...</div>}>
            {currentView === "dashboard" && <DashboardTab />}
            {currentView === "analytics" && <AnalyticsTab />}
            {currentView === "clients" && <ClientsTab />}
            {currentView === "transactions" && <TransactionsTab />}
            {currentView === "railway" && <RailwayChallansTab />}
            {currentView === "railway-coaching" && <RailwayCoachingTab />}
            {currentView === "admin" && <AdminPanelTab />}
            {currentView === "settings" && <SettingsTab />}
            {currentView === "company-profile" && <AdminPanelTab initialTab="company" />}
            {currentView === "custody" && <CustodyPilotTab />}
          </Suspense>
        </main>
      </div>
      <MobileBottomNav
        currentView={currentView}
        setCurrentView={setCurrentView}
      />
    </div>
  );
};

// --- SIGN-IN ROUTE WRAPPER ---
// Shown at "/" and "/login". If a valid session already exists, redirects to the
// appropriate dashboard instead of showing the sign-in form again.
const SignInRoute = ({ session }) => {
  const location = useLocation();
  // If there's an active session, redirect away from sign-in
  if (session) {
    // We don't know role yet here, so just go to /dashboard;
    // AppContent will handle client → /portal redirect
    const intended = location.state?.from || "/dashboard";
    return <Navigate to={intended} replace />;
  }
  return <AuthPage />;
};

// --- PROTECTED ROUTE WRAPPER ---
// Any route that requires authentication. If no session, sends to sign-in.
const ProtectedRoute = ({ session, children }) => {
  const location = useLocation();
  if (!session) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }
  return children;
};

// --- MAIN APP ENTRY ---
// Single BrowserRouter wraps everything — sign-in and app share the same
// router context so back/forward always works and history is never lost.
function App() {
  const [session, setSession] = useState(undefined); // undefined = still loading

  useEffect(() => {
    let settled = false;
    // 5-second hard timeout prevents infinite spinner if Supabase hangs
    const timeout = setTimeout(() => {
      if (!settled) { settled = true; setSession(null); }
    }, 5000);

    supabase.auth.getSession()
      .then(({ data: { session: s } }) => {
        if (!settled) { settled = true; clearTimeout(timeout); setSession(s ?? null); }
      })
      .catch(() => {
        if (!settled) { settled = true; clearTimeout(timeout); setSession(null); }
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s ?? null);
      if (!settled) { settled = true; clearTimeout(timeout); }
    });

    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, []);

  // Still determining session — show minimal loading screen
  if (session === undefined) {
    return (
      <div className="h-screen flex items-center justify-center text-slate-500 text-sm">
        Loading...
      </div>
    );
  }

  return (
    <LoadingBarProvider>
      <CompanyProfileProvider>
        <AuthProvider session={session}>
          <BrowserRouter>
            <Routes>
              {/* Sign-in: "/" and "/login" always show sign-in unless already authenticated */}
              <Route path="/" element={<SignInRoute session={session} />} />
              <Route path="/login" element={<SignInRoute session={session} />} />

              {/* All protected routes — no session → redirected to "/" */}
              <Route path="/portal" element={<ProtectedRoute session={session}><AppContent /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute session={session}><AppContent /></ProtectedRoute>} />
              <Route path="/clients" element={<ProtectedRoute session={session}><AppContent /></ProtectedRoute>} />
              <Route path="/transactions" element={<ProtectedRoute session={session}><AppContent /></ProtectedRoute>} />
              <Route path="/railway" element={<ProtectedRoute session={session}><AppContent /></ProtectedRoute>} />
              <Route path="/railway-coaching" element={<ProtectedRoute session={session}><AppContent /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute session={session}><AppContent /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute session={session}><AppContent /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute session={session}><AppContent /></ProtectedRoute>} />
              <Route path="/company-profile" element={<ProtectedRoute session={session}><AppContent /></ProtectedRoute>} />
              <Route path="/custody" element={<ProtectedRoute session={session}><AppContent /></ProtectedRoute>} />

              {/* Anything else — if logged in go to dashboard, otherwise sign-in */}
              <Route path="*" element={
                session
                  ? <Navigate to="/dashboard" replace />
                  : <Navigate to="/" replace />
              } />
            </Routes>
            <Toaster />
          </BrowserRouter>
        </AuthProvider>
      </CompanyProfileProvider>
    </LoadingBarProvider>
  );
}

export default function AppWithAuth() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </ErrorBoundary>
  );
}
